// Purpose: generally complex functions that export to ContentEditorStore.
// Imports from same package: any.
// Imports to same package: NONE

import {Map, List} from 'immutable';
import {marked} from 'marked';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {getObject} from '@packages/utilities/json';
import {insert} from '@packages/utilities/string-utils';
import {Box} from '@packages/utilities/box';
import {
  Component,
  ExpressionPath,
  Expressions,
  Stoppable,
  matchExpression,
  Plugins,
} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  ERecord,
  EText,
  ELiteral,
  Expression,
  type ExpressionElement,
  EBoolean,
} from '@packages/systems/wfdl-ast';
import {
  parseNumber,
  bindValue,
  clamp,
  formatNumberWithConstraints,
} from '@packages/systems/core/utils/NumberUtils';
import {
  addElementAtAnchor,
  getChildren,
  removeElementById,
  filterElementTree,
  getCurrentComponent,
  getClosestListElement,
  getClosestListItemElement,
  getNext,
  getPrev,
  getTag,
  getTagFromData,
  getPageComponentRootElement,
  findFurthestAncestor,
  addElementAtAnchorById,
  findElementDescendant,
  getClosestFigureElement,
  findElement,
  findIndexById,
  findAllDescendantsFromExpression,
  getKeyPathToElement,
  updateElementListAtPath,
  getCurrentComponentType,
  isUserComponent,
} from '@packages/utilities/platform';
import {updateElementData} from '@packages/utilities/platform-elementdata';
import {
  isList,
  isFigure,
  isIframeElement,
  isLink,
  isLinkElement,
  isParagraph,
  isRichText,
  isStringElement,
  isDivBlock,
  isStrong,
  isEmphasized,
  isImageElement,
  isListItem,
  isCodeBlock,
  isEmbed,
  isFigCaption,
  isLineBreakElement,
  isHtmlEmbed,
} from '@packages/utilities/elementTypeUtils';
import {isBlockElement} from '@packages/utilities/platform-elements';
import {isDynamoChildrenBinding} from '@packages/systems/dynamo/wfdl';
import {RichTextValue} from '@packages/systems/dynamo/binding-props';
import {
  CONTENT_EDITOR_CHARS_INSERTED,
  CONTENT_EDITOR_TEXT_VALUE_REPLACED,
  CONTENT_EDITOR_ELEMENT_ADDED,
  CONTENT_EDITOR_KEY_DOWN,
  CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED,
} from '@packages/systems/designer/actions';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {selectIsEditMode} from '@packages/systems/designer-selectors';
import {type DesignerStores} from '@packages/systems/designer/flux';
import {
  ContentEditorActive,
  ContentEditorUndoStackItemRecord,
  ContentEditorUndoStackItemRecordType,
} from '@packages/shared/states';
import {pipe, not, concat, emptyArray} from '@packages/utilities/fp/utils';
import {dissoc} from '@packages/utilities/key-map';
import {maybe} from '@packages/utilities/fp/option';
import {
  ActiveUploadRecord,
  ActiveUploadRecordType,
  NodeSelectionRecord,
  RangeSelectionRecord,
  RangeSelectionRecordType,
  SelectionRecordType,
} from '@packages/shared/records';
import {getAspectRatio} from '@packages/systems/core/utils/FigureUtils';
import {incrementGuid} from '@packages/utilities/guid';
import {identity} from 'lodash';
import {Tree} from '@packages/systems/tree';
import {
  QualifiedMap,
  getName,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  ConversionTypeEnum,
  rteHtmlToJsonNodes,
  rteJsonNodesToHtml,
  getRichTextValueData,
} from '@packages/systems/core/utils/JsonDomUtils';
import {Pair, pair, getSecond, getFirst} from '@packages/utilities/Pair';
import {
  dataTypesFromComponents,
  reifyElementTree,
} from '@packages/systems/core/render';
import {NativeId, getGuid} from '@packages/systems/core/utils/NativeIdUtils';
import {ParsedSelectionType} from '@packages/shared/types/ContentEditor';
import {ZWJ_RAW, isEmptyValue} from '@packages/systems/core/utils/TextUtils';
import {AI_CONTENT_GENERATION_ENTRY} from '@packages/systems/ai/writingAssistant';
import {
  SPACE,
  BACKSPACE,
  DELETE,
  UP,
  RIGHT,
  DOWN,
  LEFT,
  OPENING_PARENTHESES,
} from '@packages/utilities/key-utils';
import {isWebflowImage} from '@packages/systems/core/utils/UrlUtils';
import {paths} from '@packages/systems/core/constants/SharedConfig';
import {
  TOOLBARS,
  SHORTCUTS,
  PASTE_SELECTION_MARKER,
} from '@packages/shared/constants/ContentEditor';
import {FIGCAPTION_PLACEHOLDER_TEXT} from '@packages/shared/constants/Render';
import {
  CONTENT_EDITOR_ADD_TYPES,
  ContentEditorActiveAndNextId,
  ADD_IMAGE,
  ADD_MEDIA,
  ADD_AI_PARAGRAPH,
  CONTENT_EDITOR_MENUS,
  FORMAT_BUTTONS,
  ADD_COMPONENT,
  contentEditorCreation,
  createEmptyString,
  createLineBreak,
  createParagraphWithZwj,
  createParagraphWithText,
  createStringElement,
  instantiateElement,
  createWhitespaceString,
  assertNodeSelectionRecordType,
  assertRangeSelectionRecordType,
  caretIsAtEndOfTextNode,
  caretIsAtStartOfTextNode,
  changeCaretOffsetBy,
  collapseTextSelection,
  collapseTextSelectionAtLeft,
  getLeftRightPosition,
  getNodeIdFromSelection,
  getSelectedElements,
  isCollapsed,
  isStringOrLineBreak,
  reifySelectionRecord,
  selectLinkElement,
  selectNode,
  selectStartOfString,
  setClientRect,
  setSelection,
  setRightEndOfSelection,
  updateSelection,
  trimNodesInsideSelection,
  findDeepFigureChildElement,
  canHaveLinksInContent,
  getClosestTopLevelElement,
  getClosestTopLevelTypoElement,
  getLeftDeepestTextOrLineBreak,
  getRightDeepestTextOrLineBreak,
  getLeftDeepLink,
  isAiGenerationSupportedElement,
  isFormatElement,
  isRichTextNonFormatTypographic,
  isRichTextTopLevelTypographic,
  isNonFormatTypographic,
  selectTextElement,
  idPassthrough,
  applyPendingFormat,
  bumpCaretAroundZWJChar,
  caretIsAtTheStartOfTheNode,
  caretIsAtTheEndOfTheNode,
  deeplyJoinNodes,
  getFigureType,
  setAiData,
  setSubtree,
  unwrapSelection,
  updateParentChildren,
  updateSubtree,
  setStringValue,
  insertElement,
  getTextValue,
  isEmptyTypoOrLineBreak,
  isEmptyLinkElement,
  handleBackspaceAtStartOfFigcaption,
  handleBackspaceAtStartOfListItem,
  handleList,
  hideToolbar,
  patchLineBreakElements,
  findFurthestFormatElement,
  findMoveTarget,
  moveToFormatNodeSibling,
  moveToTargetOrAddParagraph,
  enforceTagStructure,
  enforceTagStructureScoped,
  type TopLevelRichTextRange,
  insertElementsAfterClosestTopLevel,
  getFigureIdFromSelection,
  getFigureTypeById,
  setImageAltText,
  addParagraphWithEmptyString,
  bumpSelectionEdgesIntoNearbyTextNodes,
  mergeElementsSubtree,
  moveCaretToDeepStartOfNode,
  moveCaretToDeepEndOfNode,
  moveToDeepLeftSiblingOrCreateParagraph,
  moveToDeepSibling,
  resetSelection,
  selectNewElement,
  toggleSelectionFormat,
  getFigcaptionForFigure,
  figcaptionIsPlaceholder,
  replaceElement,
  removeElement,
  setChildren,
  setConsecutiveSpacebarAttempts,
  shouldMerge,
  insertChars,
  splitList,
  splitNodeOnCollapsedRangeInTextNode,
  getAllTextInElement,
  handleLinkDefaults,
  removeConsecutiveSpaces,
  cleanUpGhostLinebreaksAndEmptyText,
  setInFigureMaxWidth,
  setInFigureMaxWidthStyle,
  setInFigureWidthStyle,
  setInFigureAlign,
  updateElementById,
  updateLinkNode,
  updateChildren,
  zwjString,
  mergeWithAdjacentSiblings,
  setShouldCloseAfterComponentAdd,
  setShouldSyncToComponentMap,
  setRemovedComponentIds,
  setAddedComponentIds,
  setComponentChangeSource,
  isEditingCMSOnCanvas,
  isEditingDynamicCMSContent,
  isEditingRichTextComponentProp,
  isNumberEditing,
  needsContentEditorPlaintextMode,
} from '@packages/systems/ContentEditor';
import type {IframeFigureData} from './types';
import {AiFieldGenerationSucceededResponse} from './actions';
import {CONTENT_EDITOR_AI_TOOLBARS} from './constants';
import type {ComponentMetadata} from '@packages/systems/component-utils';

const {EXAMPLE_IMG_URL} = paths;

export const setNativeRender =
  (allow: boolean) => (state: ContentEditorActive) =>
    state.set('allowNativeRender', allow);

export const shouldUseReactRender = setNativeRender(false);

/**
 * Single source of truth for whether the content editor should route typing
 * through the native-render fast path or force a React reconcile.
 *
 * Excluded contexts (always React render unless explicitly supported):
 * - CMS-bound fields: validation, error states, save flows
 * - Number fields: min/max clamping on blur
 */
export const shouldKeepNativeRenderForTyping = (
  state: ContentEditorActive,
  {
    allowCmsBoundRichTextNativeRender = false,
  }: {allowCmsBoundRichTextNativeRender?: boolean} = {}
): boolean =>
  !isNumberEditing(state) &&
  (!isEditingCMSOnCanvas(state) ||
    (allowCmsBoundRichTextNativeRender &&
      state.field?.type === 'RichText' &&
      isRichText(state.subtree) &&
      !state.componentProp &&
      !state.isDataManagerEditing &&
      !state.fromModal));

/*
Replace temporary placeholder text when clicking a figcaption with placeholder text
and replacing its value with ZWJ_RAW. We also move the caret to the start of that node.
*/
export const replaceTemporaryPlaceholderText =
  (figureWithPlaceholderFigcaption: ExpressionElement) =>
  (state: ContentEditorActive) => {
    const {selection, subtree} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);
    // if caret is within the figure with placeholder figcaption, replace the figcaption text with ZWJ_RAW so the user can replace the placeholder text
    // note that if the figure itself is selected (i.e. its a nodeSelection not a rangeSelection) we want to still show the placeholder
    const selectedElement = rangeSelection
      ? getSelectedElements(subtree, rangeSelection)
      : [];

    const isFigureWithPlaceholderFigCaptionSelected = selectedElement.includes(
      figureWithPlaceholderFigcaption
    );

    if (isFigureWithPlaceholderFigCaptionSelected) {
      const stringElement = findElementDescendant(
        figureWithPlaceholderFigcaption,
        isStringElement
      );
      if (!stringElement) return state;

      return pipe([
        updateSubtree(setStringValue(stringElement.id, ZWJ_RAW)),
        setSelection(
          RangeSelectionRecord({
            anchorNodeId: stringElement.id,
            focusNodeId: stringElement.id,
            anchorOffset: 0,
            focusOffset: 0,
            leftToRight: true,
          })
        ),
      ])(state);
    }

    return state;
  };

const combineImageTypedElementResults =
  (result: boolean) =>
  (nextResult: boolean): boolean =>
    result || nextResult;

const hasImageTypedDescendant = Expressions.everything<boolean>(
  combineImageTypedElementResults
)((expression) => {
  const element = Expressions.getElement(expression);
  return element && getFigureType(element) === 'image'
    ? Expressions.stop(true)
    : Expressions.resume(false);
});

const getFigureWithPlaceholderFigcaption = (
  subtree: ExpressionElement
): ExpressionElement | null => {
  return findElement(subtree, (figure: ExpressionElement): boolean => {
    if (isFigure(figure) && getFigureType(figure) === 'image') {
      const figcaption = getFigcaptionForFigure(subtree, figure.id) ?? false;
      return figcaption && figcaptionIsPlaceholder(figcaption);
    }
    return false;
  });
};

const removeFigCaption = (
  figcaptionForFigureId: string,
  subtree: ExpressionElement
): ExpressionElement => {
  const updatedSubtree = removeElementById(
    EElement(subtree),
    figcaptionForFigureId
  );
  return Expressions.getElement(updatedSubtree) ?? subtree;
};

// Hide any placeholder figcaption that does not belong to our selected figure.
const hideUnselectedPlaceholderFigcaptions =
  (figureWithPlaceholderFigcaption: ExpressionElement) =>
  (state: ContentEditorActive) => {
    const {selection, subtree} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);
    const selectedElements = rangeSelection
      ? getSelectedElements(subtree, rangeSelection)
      : [];

    const figcaptionForFigure = getFigcaptionForFigure(
      subtree,
      figureWithPlaceholderFigcaption.id
    );

    const isFigCaptionWithinSelectedElements =
      figcaptionForFigure && selectedElements.includes(figcaptionForFigure);
    const isFigCaptionWithinSelectedFigure =
      assertNodeSelectionRecordType(selection)?.nodeId ===
      figureWithPlaceholderFigcaption.id;

    const figCaptionIsSelected =
      isFigCaptionWithinSelectedElements || isFigCaptionWithinSelectedFigure;

    if (!figCaptionIsSelected) {
      const updatedSubtree = hidePlaceholderFigcaption(subtree);

      // We only update the subtree if the placeholder figcaption is not part of the current selection
      return setSubtree(updatedSubtree)(state);
    }
    return state;
  };

// If we add/remove the figcaption, we want the client rect to be refreshed
// so the toolbar positioned is calculated with the final figcaption status
// of the toggle
const recalculateClientRect =
  ({originalState}: {originalState: ContentEditorActive}) =>
  (state: ContentEditorActive) => {
    if (originalState.subtree !== state.subtree) {
      const rectName =
        state.selection.type === 'Range' ? 'rangeClientRect' : 'nodeClientRect';

      return state.setIn(['selection', rectName], null);
    } else {
      return state;
    }
  };

// Looks for a remaining figure with a placeholder figcaption inside of the subtree to remove it
// There shouldn't be more than one figcaption active, so, we only look for one.
export const hidePlaceholderFigcaption = (
  subtree: ExpressionElement
): ExpressionElement => {
  const figureWithPlaceholderFigcaption =
    getFigureWithPlaceholderFigcaption(subtree);

  if (figureWithPlaceholderFigcaption) {
    const figcaptionForFigure = getFigcaptionForFigure(
      subtree,
      figureWithPlaceholderFigcaption.id
    );

    if (!figcaptionForFigure) {
      return subtree;
    }

    if (!figcaptionIsPlaceholder(figcaptionForFigure)) {
      return subtree;
    }

    return removeFigCaption(figcaptionForFigure.id, subtree);
  }
  return subtree;
};

const getClosestFigureFromSelection = (
  subtree: ExpressionElement,
  selection: SelectionRecordType
): ExpressionElement | null => {
  if (selection.type === 'Node' && selection.nodeId) {
    const figureElement = Expressions.getElementById(
      EElement(subtree),
      selection.nodeId
    );
    return figureElement ?? null;
  }

  if (selection.type === 'Range') {
    const figureElement = getClosestFigureElement(
      EElement(subtree),
      selection.anchorNodeId
    );
    if (figureElement) {
      return figureElement;
    }
  }
  return null;
};

const placeholderFigCaptionElement = {
  id: 'newRightSideValue String',
  type: qualify(Plugins.name('Basic'), Component.name('Figcaption')),
  data: ERecord({
    children: EList([
      EElement({
        id: 'FigCaption String',
        type: qualify(Plugins.name('Basic'), Component.name('String')),
        data: EText(FIGCAPTION_PLACEHOLDER_TEXT),
      }),
    ]),
  }),
};

// If there's a figure selected, we add its figcaption
const showSelectedFigurePlaceholderFigcaption = (
  stateAndNextId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const [state, nextId] = stateAndNextId;
  const {subtree, selection} = state;
  const figure = getClosestFigureFromSelection(subtree, selection);
  const isImageFigure = figure && getFigureType(figure) === 'image';

  if (isImageFigure) {
    const figcaptionElement = getFigcaptionForFigure(subtree, figure.id);

    // already has figcaption, do not need to add another
    if (figcaptionElement) {
      return stateAndNextId;
    }

    const [{id: nextSeedId}, newFigCaption] = instantiateElement(
      placeholderFigCaptionElement,
      nextId
    );

    return [
      setSubtree(
        Expressions.getElement(
          addElementAtAnchorById(
            EElement(subtree),
            newFigCaption,
            figure.id,
            'append'
          )
        ) || subtree
      )(state),
      nextSeedId,
    ];
  }
  return stateAndNextId;
};

export const togglePendingFormat = (format: 'bold' | 'italic') =>
  pipe([
    idPassthrough((state) =>
      state.set('formatToggle', state.formatToggle.update(format, not))
    ),
    unwrapSelection(FORMAT_BUTTONS.subscript),
  ]);

/*
Toggles a placeholder figcaption when selecting

Overview:
1. Filters the subtree for all figures with a figcaption
2. We check if this figure subtree includes an unselected figure with a figcaption
   that has either placeholder or empty text (figureWithPlaceholderFigcaption).
3. If it does, we remove the temporary placeholder figcaption node.
4. We then check if there is a figure selected and if a figcaption already exists for it.
5. If it doesn't, it creates a text node with the figcaption placeholder text and its parent node with a figcaption tag.
4. We then append it to the selected figure node in the current subtree
*/
export const togglePlaceholderFigcaption = (
  activeStateAndNextId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const [state] = activeStateAndNextId;
  const {subtree} = state;

  if (
    getFigureType(subtree) !== 'image' &&
    !hasImageTypedDescendant(subtree.data)
  )
    return activeStateAndNextId;

  const figureWithPlaceholderFigcaption =
    getFigureWithPlaceholderFigcaption(subtree);

  if (figureWithPlaceholderFigcaption) {
    return pipe([
      idPassthrough(
        replaceTemporaryPlaceholderText(figureWithPlaceholderFigcaption)
      ),
      idPassthrough(
        hideUnselectedPlaceholderFigcaptions(figureWithPlaceholderFigcaption)
      ),
      showSelectedFigurePlaceholderFigcaption,
      idPassthrough(recalculateClientRect({originalState: state})),
    ])(activeStateAndNextId);
  } else {
    return pipe([
      showSelectedFigurePlaceholderFigcaption,
      idPassthrough(recalculateClientRect({originalState: state})),
    ])(activeStateAndNextId);
  }
};

export const setActiveToolbar =
  (toolbar: null | string) => (state: ContentEditorActive) =>
    state.set('activeToolbar', toolbar);

export const getActiveToolbarForSelection = (
  state: ContentEditorActive
): string | null => {
  const {subtree, selection} = state;

  // Don't show any toolbars for number fields
  if (isNumberEditing(state)) {
    return null;
  }

  if (needsContentEditorPlaintextMode(state)) {
    // We now show the AI toolbars inside bound Content Editor plain text mode.
    // So let those toolbars stay active if they're already open.
    const {activeToolbar} = state;
    if (
      activeToolbar &&
      (CONTENT_EDITOR_AI_TOOLBARS as Set<string>).has(activeToolbar)
    ) {
      return activeToolbar;
    }

    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return null;

    // we only want to show the regenerate AI button inside bound CMS
    if (!isCollapsed(rangeSelection)) return TOOLBARS.AI_ONLY;

    // We don't want to show any toolbars for plaintext DynBound typographic
    // nodes inside the Editor Canvas
    return null;
  }

  if (selection.type === 'Node') {
    if (selection.nodeId) {
      const el = Expressions.getElementById(subtree.data, selection.nodeId);

      if (!el) return null;
      if (isFigure(el)) return TOOLBARS.FIGURE;
      if (isHtmlEmbed(el)) return TOOLBARS.EMBED;
      if (isCodeBlock(el)) return TOOLBARS.CODEBLOCK;
      if (
        (isEditingRichTextComponentProp(state) ||
          isEditingDynamicCMSContent(state)) &&
        isUserComponent(el.type)
      )
        return TOOLBARS.COMPONENT;

      return null;
    }
    return null;
  }

  if (isCollapsed(selection)) {
    if (!isRichText(subtree)) return null;
    // Range selection
    // With a collapsed selection we usually show the link toolbar when the caret is somewhere inside a link,
    // but if the root of the subtree is itself a link in the designer-canvas that would be annoying, so in
    // that case the user would have to select text to show the format toolbar, then click the link button
    // or use the cmd+k shortcut to show it.
    // Find the text node where the caret is
    const subtreeExpression = EElement(subtree);
    const anchorPath = getKeyPathToElement(
      subtreeExpression,
      selection.anchorNodeId
    );
    // Guard against certain race conditions more gracefully.
    if (!anchorPath) return null;

    let currentExpression: Expression | undefined = subtreeExpression;
    const ancestors: ExpressionElement[] = [subtree];

    for (const pathPart of anchorPath) {
      currentExpression = Expressions.getIn(currentExpression, [pathPart]);
      const ancestor = currentExpression
        ? Expressions.getElement(currentExpression)
        : null;

      if (ancestor) ancestors.push(ancestor);
    }

    const anchorNode = currentExpression
      ? Expressions.getElement(currentExpression)
      : null;
    if (!anchorNode) return null;

    if (ancestors.slice(1).some(isLinkElement)) return TOOLBARS.LINK;

    const anchorValue = Expressions.getText(anchorNode.data);

    if (isEmptyValue(anchorValue)) {
      const parentNode = ancestors.at(-2) ?? null;

      const parentChildren = parentNode ? getChildren(parentNode) || [] : [];

      if (parentNode && isParagraph(parentNode) && parentChildren.length === 1)
        return CONTENT_EDITOR_MENUS.ADD_PLUS_BUTTON;

      if (
        parentNode &&
        isListItem(parentNode) &&
        parentChildren.length > 0 &&
        parentChildren.every((el) => !isList(el))
      )
        return TOOLBARS.LIST;
    }

    // We toggle the slash command menu to active in CONTENT_EDITOR_SLASH_COMMAND_ACTIVATED, but here, we determine if it should stay active
    if (state.activeToolbar === CONTENT_EDITOR_MENUS.ADD_MENU) {
      const hasSlashCommand = Boolean(getSlashCommand(state));
      return hasSlashCommand ? CONTENT_EDITOR_MENUS.ADD_MENU : null;
    }

    return null;
  }

  // it's not collapsed, so there must be a text selected.

  // If the AI Edit Menu is already open, keep it open.
  if (state.activeToolbar === CONTENT_EDITOR_MENUS.AI_EDIT_MENU) {
    return CONTENT_EDITOR_MENUS.AI_EDIT_MENU;
  }

  return TOOLBARS.FORMAT;
};

export const setActiveToolbarFromState = (state: ContentEditorActive) => {
  return pipe([
    setActiveToolbar(getActiveToolbarForSelection(state)),
    updateSelection(setClientRect(null)),
  ])(state);
};

export const clearPendingLinkToolbarChanges = (state: ContentEditorActive) =>
  state.set('pendingLinkToolbarChanges', null);

export const enableLinkOnlyMode = (state: ContentEditorActive) =>
  state.set('linkOnlyMode', true);

// eslint-disable-next-line webflow/selector-memoization -- Not a UI selector
const selectFigure = (figureId: string) =>
  pipe([selectNode(figureId), setActiveToolbar(TOOLBARS.FIGURE)]);

export const removeLink =
  (linkId: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {subtree, selection: currentSelection} = getFirst(stateAndId);
    const rangeSelection = assertRangeSelectionRecordType(currentSelection);

    const link = Expressions.getElementById(EElement(subtree), linkId);
    if (!link) return stateAndId;
    // Note: even if it's outside the selection it's possible for nodes to be merged and removed
    // so the previous selection is just invalid.
    // Ultimately we should add the ability to unwrap a node without first selecting it
    const newSelectionIsOutsideLink =
      rangeSelection &&
      !Expressions.getElementById(
        EElement(link),
        rangeSelection.anchorNodeId
      ) &&
      !Expressions.getElementById(EElement(link), rangeSelection.focusNodeId);

    const unwrapLinkSelection = unwrapSelection(FORMAT_BUTTONS.link);

    if (rangeSelection && isCollapsed(rangeSelection)) {
      return pipe<ContentEditorActiveAndNextId>([
        idPassthrough(selectTextElement(linkId)),
        unwrapLinkSelection,
        idPassthrough((_state) => {
          const _subtree = EElement(_state.subtree);
          // If the user clicked somewhere while the link toolbar was open, that link would be removed.
          // It's possible after unwrapping and neighbouring nodes getting merged together that the caret's
          // parent nodes no longer exist because they were merged into something else.
          // If the user clicked somewhere else and it still exists, then put the caret where they want it
          // If the selection is not valid, just leave the previously link text selected
          // If they click inside the link then we know that collapsed selection is no longer valid because
          // the link was unwrapped.
          // Note: maybe one day we can come up with a way to track the alternate selection as we unwrap
          // but until then it's just an acceptable edge cases
          const selectionStillValid =
            Expressions.getElementById(_subtree, rangeSelection.anchorNodeId) &&
            Expressions.getElementById(_subtree, rangeSelection.focusNodeId);

          if (newSelectionIsOutsideLink && selectionStillValid)
            return _state.set('selection', currentSelection);

          return _state;
        }),
      ])(stateAndId);
    }

    return pipe([
      unwrapLinkSelection,
      idPassthrough(setActiveToolbar(TOOLBARS.FORMAT)),
    ])(stateAndId);
  };

export const removeLinkIfEmpty =
  (linkId: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {subtree} = getFirst(stateAndId);
    const link = Expressions.getElementById(subtree.data, linkId);
    if (!link || !isEmptyLinkElement(link)) return stateAndId;
    return removeLink(linkId)(stateAndId);
  };

// Wraps the selected figure node inside a new parent link node and replaces it in the subtree
export const wrapFigureInLink =
  (figureId: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const {subtree} = state;
    // Find the image inside of the figure
    const imgNode = findDeepFigureChildElement(subtree, figureId);
    if (!imgNode) return stateAndId;
    // We should be wrapping the parent div of the img inside of the figure
    const imgParentNode = Expressions.getParent(EElement(subtree), imgNode.id);
    if (!imgParentNode) return stateAndId;

    const linkId = getSecond(stateAndId);

    const newLinkNode = {
      id: linkId,
      type: qualify(Plugins.name('Basic'), Component.name('Link')),
      data: ERecord({
        link: ELiteral({
          name: qualify(Plugins.name('Basic'), typeName('Link')),
          value: {mode: 'external', url: ''},
        }),
        children: EList([EElement(imgParentNode)]),
      }),
    };

    return pair(
      updateSubtree(replaceElement(imgParentNode.id, newLinkNode))(state),
      incrementGuid(linkId)
    );
  };

export const handleFigureToolbarLink = (
  state: ContentEditorActive,
  href: string,
  figureId: string,
  seedId: string
) => {
  if (!figureId) return state;
  if (!state.selection) return state;

  return getFirst(
    pipe<ContentEditorActiveAndNextId>([
      // Add figure link if needed
      (stateAndId) => {
        const {subtree} = getFirst(stateAndId);
        const linkNode = getLeftDeepLink(subtree, figureId);
        const parent = linkNode
          ? Expressions.getParent(EElement(subtree), linkNode.id)
          : null;

        // We need to check if the link found inside of the selected subtree belongs
        // to the figcaption. In this case, the figure is not linked yet
        const linkIsInsideFigcaption = parent ? isFigCaption(parent) : null;

        if (!linkNode || linkIsInsideFigcaption)
          return wrapFigureInLink(figureId)(stateAndId);

        return stateAndId;
      },
      (stateAndId) => {
        const {subtree} = getFirst(stateAndId);
        const linkNode = getLeftDeepLink(subtree, figureId);
        if (!linkNode) return stateAndId;
        if (!href) return removeLink(linkNode.id)(stateAndId);

        const target = '_blank';

        const newLinkNode = updateElementData((data) => {
          const props = Expressions.getRecord(data);
          if (!props) return data;
          return ERecord({
            ...props,
            link: ELiteral({
              name: qualify(Plugins.name('Basic'), typeName('Link')),
              value: {mode: 'external', url: href, target},
            }),
            block: EText('inline'),
          });
        })(linkNode);

        return idPassthrough(
          updateSubtree(replaceElement(linkNode.id, newLinkNode))
        )(stateAndId);
      },
    ])(pair(state, seedId))
  );
};

export const toggleLinkFormatButton = (
  stateAndNextId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const [state, nextId] = stateAndNextId;
  const {subtree, selection, breakOutSpanLinks} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  const selectedElements = rangeSelection
    ? getSelectedElements(subtree, rangeSelection)
    : [];
  const linkElements = selectedElements.filter(isLinkElement);
  const linkElement = linkElements[0];

  if (linkElement) {
    if (breakOutSpanLinks) {
      // Note: In the designer, for simple text we prefer to "breakOut" of the ContentEditor and
      // pass control to the designer settings panel for editing links.
      return [state.set('breakOutElement', linkElement), nextId];
    } else {
      return [
        pipe([
          selectLinkElement(linkElement),
          setActiveToolbar(TOOLBARS.LINK),
          shouldUseReactRender,
        ])(state),
        nextId,
      ];
    }
  }

  return pipe([
    idPassthrough(setActiveToolbar(TOOLBARS.LINK)),
    toggleSelectionFormat(FORMAT_BUTTONS.link),
  ])(stateAndNextId);
};

// Gets the text in current node and determines if can show a slash command and if there is one.
// If so, it returns the command. If not, it returns null.
export const getSlashCommand = (state: ContentEditorActive): string | null => {
  const {subtree, selection} = state;

  if (
    state.activeToolbar !== CONTENT_EDITOR_MENUS.ADD_MENU ||
    !isRichText(subtree)
  ) {
    return null;
  }

  const rangeSelection = assertRangeSelectionRecordType(selection);

  if (!rangeSelection) {
    return null;
  }

  const anchorNode = Expressions.getElementById(
    EElement(subtree),
    rangeSelection.anchorNodeId
  );

  if (!anchorNode) {
    return null;
  }

  const textValue = getTextValue(anchorNode);

  // Get the text before the caret and find the closest forward slash
  // If there isn't a forward slash or there is a space, it's not a slash command
  const textBeforeCaret = textValue.slice(0, rangeSelection.focusOffset);
  const closestForwardSlash = textBeforeCaret.lastIndexOf('/');
  if (
    closestForwardSlash === -1 ||
    textBeforeCaret.slice(closestForwardSlash).includes(' ')
  ) {
    return null;
  }

  // If it gets this far, there is a valid slash command. Return the filter text.
  return textBeforeCaret.slice(closestForwardSlash);
};

// Uses getSlashCommand to get a slash command filter. If it not null, it updates the state.
export const setSlashCommandFilter = (state: ContentEditorActive) => {
  const filter = getSlashCommand(state);

  return filter === null
    ? state
    : state.set('slashCommand', {
        ...state.slashCommand,
        filter: filter.slice(1),
      });
};

export const addElementInValidPosition =
  (newElement: ExpressionElement) =>
  (state: ContentEditorActive): ContentEditorActive => {
    const selectedNodeId = getNodeIdFromSelection(state.selection);

    if (!selectedNodeId) {
      console.error('no node selected in rich text');
      return state;
    }

    const validAnchor = getClosestTopLevelElement(
      selectedNodeId,
      state.subtree
    );

    if (!validAnchor) {
      console.error('no valid anchor found');
      return state;
    }

    return setSubtree(
      Expressions.getElement(
        addElementAtAnchor(
          EElement(state.subtree),
          newElement,
          validAnchor,
          'after'
        )
      ) || state.subtree
    )(state);
  };

const removeEmptyElement =
  (elementId: string) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const element = Expressions.getElementById(EElement(subtree), elementId);
    if (element === null) return subtree;
    if (isEmptyTypoOrLineBreak(element)) {
      return (
        Expressions.getElement(
          removeElementById(EElement(subtree), elementId)
        ) || subtree
      );
    }
    return subtree;
  };

const removeSlashCommandText = (state: ContentEditorActive) => {
  const {slashCommand} = state;

  const slashCommandText = slashCommand?.filter;
  if (slashCommandText === null) {
    return state;
  }

  const selection = slashCommand?.selection;

  if (selection === null) {
    return state;
  }

  const updatedSubtree = Expressions.getElement(
    Expressions.updateElement(
      selection.focusNodeId,
      (element) => {
        if (isStringElement(element)) {
          const elementData = Expressions.getElementData(element);
          const text = Expressions.getText(elementData);

          if (!text) return element;

          const startOfSlashCommand = selection.focusOffset;

          const endOfSlashCommand =
            startOfSlashCommand + slashCommandText.length;

          const result =
            text.slice(0, startOfSlashCommand - 1) +
            text.slice(endOfSlashCommand);

          return Expressions.setElementData(element, EText(result));
        }
        return element;
      },
      EElement(state.subtree)
    )
  );

  return updatedSubtree ? setSubtree(updatedSubtree)(state) : state;
};

// we do not want to delete anything if it is a fig caption
const removeAnchorIfTopTypographic =
  (element: ExpressionElement) =>
  (subtree: ExpressionElement): ExpressionElement => {
    if (isRichTextTopLevelTypographic(element)) {
      return removeEmptyElement(element.id)(subtree);
    }
    return subtree;
  };

export const addSlashCommandElementAfterTopLevel =
  (newElement: ExpressionElement) => (state: ContentEditorActive) => {
    const selectedNodeId = getNodeIdFromSelection(state.selection);
    const {subtree} = state;

    if (!selectedNodeId) {
      console.error('no node selected in rich text');
      return state;
    }

    const validAnchor = getClosestTopLevelElement(selectedNodeId, subtree);

    if (!validAnchor) {
      console.error('no valid anchor found');
      return state;
    }

    return pipe([
      setSubtree(
        Expressions.getElement(
          addElementAtAnchor(
            EElement(subtree),
            newElement,
            validAnchor,
            'after'
          )
        ) || subtree
      ),
      removeSlashCommandText,
      updateSubtree(removeAnchorIfTopTypographic(validAnchor)),
    ])(state);
  };

/**
 * Handles CONTENT_EDITOR_ELEMENT_INSERTED: inserts code and shared library components
 * into the active Rich Text. Distinct from CONTENT_EDITOR_ELEMENT_ADDED, which
 * handles user/site components and basic Rich Text content.
 */
export const handleContentEditorInsertElement =
  (element: ExpressionElement) =>
  (state: ContentEditorActive): ContentEditorActive => {
    const stateWithElementInserted = pipe([
      addSlashCommandElementAfterTopLevel(element),
      selectNewElement[ADD_COMPONENT](element.id),
      hideToolbar,
    ])(state);

    if (
      isEditingDynamicCMSContent(state) ||
      isEditingRichTextComponentProp(state)
    ) {
      return setActiveToolbar(TOOLBARS.COMPONENTSETTINGS)(
        stateWithElementInserted
      );
    }

    return setShouldCloseAfterComponentAdd(true)(stateWithElementInserted);
  };

// Updates the subtree to replace the anchor element text with the provided AI-generated text
export const updateAnchorElementWithGeneratedText =
  (generatedText: string) => (state: ContentEditorActive) => {
    const {selection, subtree} = state;

    const selectionNodeId = getNodeIdFromSelection(selection);

    if (!selectionNodeId) return state;

    const anchorElement = Expressions.getElementById(
      EElement(subtree),
      selectionNodeId
    );

    if (!anchorElement) return state;

    const currentText = Expressions.getText(
      Expressions.getElementData(anchorElement)
    );

    if (!currentText) return state;

    const updatedAnchor = Expressions.setElementData(
      anchorElement,
      EText(generatedText)
    );

    return updateSubtree(replaceElement(selectionNodeId, updatedAnchor))(state);
  };

// finds most deep left node and updates the anchor node Selection with it
const updateAnchorSelection =
  (newElementId: string) => (state: ContentEditorActive) => {
    const {subtree, selection} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return state;
    const startCollapsedSelection = moveCaretToDeepStartOfNode(newElementId)(
      state.subtree
    );

    if (!startCollapsedSelection) {
      throw Error('Unable to get selection');
    }
    const anchorNode = Expressions.getElementById(
      EElement(subtree),
      startCollapsedSelection.anchorNodeId
    );
    if (!anchorNode) {
      return state;
    }

    return state.set(
      'selection',
      RangeSelectionRecord({
        anchorNodeId: startCollapsedSelection.anchorNodeId,
        anchorOffset: startCollapsedSelection.anchorOffset,
        focusNodeId: rangeSelection.focusNodeId,
        focusOffset: rangeSelection.focusOffset,
        leftToRight: rangeSelection.leftToRight,
      })
    );
  };

// add an empty typo element at the start of the selection
const extendStartSelection = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return stateAndId;

  const guid = getSecond(stateAndId);

  const [{id: nextId}, elementToAdd] = isRichText(subtree)
    ? createParagraphWithZwj(guid)
    : createEmptyString({id: guid})(state);

  return pair(
    pipe([
      updateSubtree(insertElement(elementToAdd, subtree.id, 'prepend')),
      updateAnchorSelection(guid),
    ])(state),
    nextId
  );
};

// finds most deep right node and updates the focus node Selection with it
const updateFocusSelection =
  (newElementId: string) => (state: ContentEditorActive) => {
    const {subtree, selection} = state;

    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (!rangeSelection) {
      return state;
    }
    const endCollapsedSelection = moveCaretToDeepEndOfNode(newElementId)(
      state.subtree
    );

    if (!endCollapsedSelection) {
      throw Error('Unable to get selection');
    }
    const focusNode = Expressions.getElementById(
      EElement(subtree),
      endCollapsedSelection.focusNodeId
    );
    if (!focusNode) {
      return state;
    }

    return state.set(
      'selection',
      RangeSelectionRecord({
        anchorNodeId: rangeSelection.anchorNodeId,
        anchorOffset: rangeSelection.anchorOffset,
        focusNodeId: endCollapsedSelection.focusNodeId,
        focusOffset: endCollapsedSelection.focusOffset,
        leftToRight: rangeSelection.leftToRight,
      })
    );
  };
// add an empty typo element at the end of the selection
const extendEndSelection = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree} = state;
  const guid = getSecond(stateAndId);

  const [{id: nextId}, elementToAdd] = isRichText(subtree)
    ? createParagraphWithZwj(guid)
    : createEmptyString({id: guid})(state);

  return pair(
    pipe([
      updateSubtree(insertElement(elementToAdd, subtree.id, 'append')),
      updateFocusSelection(guid),
    ])(state),
    nextId
  );
};

const isStringContainingCandidate = (element: ExpressionElement) =>
  isRichTextTopLevelTypographic(element) ||
  isList(element) ||
  isStringElement(element);

// if the first or last are code blocks, html embed or figures, we know we have to create a new typo / sting element.
const handleNodeTypeSelection = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {subtree} = getFirst(stateAndId);
  const children = getChildren(subtree);
  if (!children) return stateAndId;
  const firstElement = children[0];
  const lastElement = children[children.length - 1];

  if (!firstElement || !lastElement) return stateAndId;

  return pipe([
    !isStringContainingCandidate(firstElement)
      ? extendStartSelection
      : identity,
    !isStringContainingCandidate(lastElement) ? extendEndSelection : identity,
  ])(stateAndId);
};

// attempt to find the deepest string elements in the selection at start and end of subtree
// This allows us to main the element tag e.g. <em> or <strong> etc.
const findDeepestStrings = (state: ContentEditorActive) => {
  const {subtree} = state;
  const startCollapsedSelection = moveCaretToDeepStartOfNode(subtree.id)(
    subtree
  );
  if (!startCollapsedSelection) return state;

  const endCollapsedSelection = moveCaretToDeepEndOfNode(subtree.id)(subtree);
  if (!endCollapsedSelection) return state;

  return state.set(
    'selection',
    RangeSelectionRecord({
      anchorNodeId: startCollapsedSelection.anchorNodeId,
      anchorOffset: startCollapsedSelection.anchorOffset,
      focusNodeId: endCollapsedSelection.focusNodeId,
      focusOffset: endCollapsedSelection.focusOffset,
      leftToRight: endCollapsedSelection.leftToRight,
    })
  );
};

// if we still have not got a string selection, create them at start or end.
// This is probably caused by LineBreaks. We can solve this long term by adapting moveCaretToDeepStartOfNode for to ignore LineBreaks and other non-typo elements.
const __HACK___handleFailureToFindDeepestStrings = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {subtree, selection} = getFirst(stateAndId);

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return stateAndId;

  const anchorNode = Expressions.getElementById(
    EElement(subtree),
    rangeSelection.anchorNodeId
  );
  const focusNode = Expressions.getElementById(
    EElement(subtree),
    rangeSelection.focusNodeId
  );

  if (!anchorNode || !focusNode) return stateAndId;

  return pipe([
    !isStringElement(anchorNode) ? extendStartSelection : identity,
    !isStringElement(focusNode) ? extendEndSelection : identity,
  ])(stateAndId);
};

// selects all elements within subtree by finding or creating strings at the start or far end of the subtree
export const selectAllSubtree = pipe([
  handleNodeTypeSelection,
  idPassthrough(findDeepestStrings),
  __HACK___handleFailureToFindDeepestStrings,
]);

const START = '19d3m3';
const STOP = '2seodqdw';
const SELECTION_AI_HTML_TAG = 'wf-selection';

const wrapChildrenInStartAndStopMarkers = (
  subtree: ExpressionElement,
  seedId: string
): Pair<ExpressionElement, string> => {
  const subtreeChildren = getChildren(subtree);

  if (subtreeChildren === undefined) return pair(subtree, seedId);

  const [startEl, stopId] = createStringElement(START, seedId);
  const [stopEl, nextId] = createStringElement(STOP, stopId);

  return pair(
    Expressions.getElement(
      Expressions.setIn(
        EElement(subtree),
        [{in: 'Element'}, {in: 'Record', at: 'children'}],
        EList([
          EElement(startEl),
          ...subtreeChildren.map(EElement),
          EElement(stopEl),
        ])
      )
    ) || subtree,
    nextId
  );
};

const addStartAndStopMarkers = (
  expr: ExpressionElement,
  selection: RangeSelectionRecordType
): ExpressionElement => {
  const {leftToRight} = selection;
  return (
    Expressions.getElement(
      Expressions.everywhere(
        // @ts-expect-error: somehow matchExpression could return undefined.
        matchExpression({
          Element: (el: ExpressionElement) => {
            if (isStringElement(el)) {
              const anchorNodeId = leftToRight
                ? selection.anchorNodeId
                : selection.focusNodeId;
              const focusNodeId = leftToRight
                ? selection.focusNodeId
                : selection.anchorNodeId;
              const anchorOffset = leftToRight
                ? selection.anchorOffset
                : selection.focusOffset;
              const focusOffset = leftToRight
                ? selection.focusOffset
                : selection.anchorOffset;
              if (el.id === anchorNodeId && el.id === focusNodeId) {
                const elementData = Expressions.getElementData(el);
                const currentText = Expressions.getText(elementData);
                if (currentText === null) return EElement(el);

                return EElement(
                  Expressions.setElementData(
                    el,
                    EText(
                      currentText.slice(0, anchorOffset) +
                        START +
                        currentText.slice(anchorOffset, focusOffset) +
                        STOP +
                        currentText.slice(focusOffset)
                    )
                  )
                );
              }

              if (el.id === anchorNodeId) {
                const elementData = Expressions.getElementData(el);
                const currentText = Expressions.getText(elementData);
                if (currentText === null) return EElement(el);
                return EElement(
                  Expressions.setElementData(
                    el,
                    EText(
                      currentText.slice(0, anchorOffset) +
                        START +
                        currentText.slice(anchorOffset)
                    )
                  )
                );
              }

              if (el.id === focusNodeId) {
                const elementData = Expressions.getElementData(el);
                const currentText = Expressions.getText(elementData);
                if (currentText === null) return EElement(el);

                return EElement(
                  Expressions.setElementData(
                    el,
                    EText(
                      currentText.slice(0, focusOffset) +
                        STOP +
                        currentText.slice(focusOffset)
                    )
                  )
                );
              }
            }

            return EElement(el);
          },
          _: identity,
        })
      )(EElement(expr))
    ) || expr
  );
};

const forSameStringSelection = (state: ContentEditorActive) => {
  const rangeSelection = assertRangeSelectionRecordType(state.selection);
  if (!rangeSelection) return state;

  const {anchorOffset, focusOffset, anchorNodeId, focusNodeId, leftToRight} =
    rangeSelection;
  const subtree = state.subtree;

  const anchorElement = Expressions.getElementById(
    EElement(subtree),
    anchorNodeId
  );

  if (!anchorElement) {
    console.error('AnchorElement not found');
    return state;
  }
  const focusElement = Expressions.getElementById(
    EElement(subtree),
    focusNodeId
  );

  if (!focusElement) {
    console.error('focusElement not found');
    return state;
  }

  const anchorElementText = Expressions.getText(
    Expressions.getElementData(anchorElement)
  );

  const focusElementText = Expressions.getText(
    Expressions.getElementData(focusElement)
  );

  if (!anchorElementText || !focusElementText) {
    console.error(
      'Expected both anchorElement and focusElement to be string elements'
    );
    return state;
  }

  const updatedAnchorText = (() => {
    if (leftToRight) {
      return (
        anchorElementText.substring(0, anchorOffset) +
        focusElementText.substring(focusOffset)
      );
    } else {
      return (
        focusElementText.substring(0, focusOffset) +
        anchorElementText.substring(anchorOffset)
      );
    }
  })();

  const _updatedSubtree = Expressions.updateElement(
    anchorElement.id,
    (el: ExpressionElement) => {
      if (!isStringElement(el)) {
        console.error('selected element is not a string');
        return el;
      }
      const elementData = Expressions.getElementData(el);
      const currentText = Expressions.getText(elementData);

      if (currentText === null) return el;
      return Expressions.setElementData(el, EText(updatedAnchorText || ''));
    },
    EElement(subtree)
  );

  const updatedSubtree = Expressions.getElement(_updatedSubtree);

  if (!updatedSubtree) {
    console.error('updatedSubtree not an element');
    return state;
  }

  return setSubtree(updatedSubtree)(state);
};

// this should only be used if you are already sure that the deep left and right nodes are strings (e.g. if you have called selectAllSubtree)
export const isAllTextSelected = (state: ContentEditorActive): boolean => {
  const {subtree} = state;

  const rangeSelection = assertRangeSelectionRecordType(state.selection);

  if (!rangeSelection) return false;

  const startCollapsedSelection = moveCaretToDeepStartOfNode(subtree.id)(
    subtree
  );

  if (
    startCollapsedSelection?.anchorNodeId !== rangeSelection.anchorNodeId ||
    startCollapsedSelection?.anchorOffset !== rangeSelection.anchorOffset
  ) {
    return false;
  }

  const endCollapsedSelection = moveCaretToDeepEndOfNode(subtree.id)(subtree);

  return (
    endCollapsedSelection?.focusNodeId === rangeSelection.focusNodeId &&
    endCollapsedSelection?.focusOffset === rangeSelection.focusOffset
  );
};

const handleAllSelected = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree} = state;
  const rangeSelection = assertRangeSelectionRecordType(state.selection);
  if (!rangeSelection) return stateAndId;
  const id = getSecond(stateAndId);
  const [child, nextId] = isRichText(subtree)
    ? createParagraphWithText('', id)
    : createStringElement('', id);

  return pair(
    setAiData({
      subtreeForAiInsertion: setChildren([child])(subtree),
      selectionForAiInsertion:
        assertRangeSelectionRecordType(
          selectStartOfString(id)(state).selection
        ) || state.aiData.selectionForAiInsertion,
    })(state),
    nextId
  );
};

// prepares the subtree for insertion of PlainText within the same string node only
const createSubtreeForPlainTextAiInsertion = (state: ContentEditorActive) => {
  const rangeSelection = assertRangeSelectionRecordType(state.selection);
  if (!rangeSelection) return state;
  if (rangeSelection.anchorNodeId === rangeSelection.focusNodeId) {
    return forSameStringSelection(state);
  }
  return state;
};

// this is used for non-cms elements to get create the component / page context for LLM.
export const prepContentEditorForAiGenerationWithComponentContext =
  (
    focusedSymbolNativeIdPath: NativeId[],
    plugins: Plugins.PluginsType,
    components: Component.ComponentMap
  ) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const {selection, subtree} = state;

    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return stateAndId;

    const currentComponent =
      focusedSymbolNativeIdPath.length > 0
        ? getCurrentComponent(focusedSymbolNativeIdPath, components)
        : getPageComponentRootElement(components);

    if (currentComponent === null) return stateAndId;

    const root =
      findFurthestAncestor(
        EElement(currentComponent),
        subtree,
        (_el) =>
          isBlockElement(_el, QualifiedMap.empty()) && _el.id !== subtree.id
      ) || currentComponent;

    const _isAllTextSelected = isAllTextSelected(state);

    const result = convertSubtreeComponentContextToHtmlForLlm({
      plugins,
      components,
      seedId: getSecond(stateAndId),
      subtree,
      root,
      selection: rangeSelection,
      allTextSelected: _isAllTextSelected,
    });

    const htmlSelectionContext = getFirst(result);
    const nextId = getSecond(result);

    if (htmlSelectionContext === null) return stateAndId;

    if (_isAllTextSelected) {
      return pipe([
        idPassthrough(
          setAiData({
            htmlSelectionContext,
          })
        ),
        handleAllSelected,
      ])(pair(state, nextId));
    } else {
      const subtreeForAiInsertion =
        createSubtreeForPlainTextAiInsertion(state).subtree;

      const selectionForAiInsertion =
        collapseTextSelectionAtLeft(rangeSelection);

      return pair(
        pipe([
          setAiData({
            subtreeForAiInsertion,
            selectionForAiInsertion,
            htmlSelectionContext,
          }),
        ])(state),
        nextId
      );
    }
  };

export const prepContentEditorForAiGeneration =
  (plugins: Plugins.PluginsType, components: Component.ComponentMap) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const {selection, subtree} = state;

    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return stateAndId;

    const _isAllTextSelected = isAllTextSelected(state);

    const htmlSelectionContextResult = convertSubtreeToHtmlForLlm({
      plugins,
      components,
      subtree,
      selection: rangeSelection,
      allTextSelected: _isAllTextSelected,
      seedId: getSecond(stateAndId),
    });
    const htmlSelectionContext = getFirst(htmlSelectionContextResult);

    if (htmlSelectionContext === null) return stateAndId;

    const nextId = getSecond(htmlSelectionContextResult);

    if (_isAllTextSelected) {
      return pipe([
        idPassthrough(
          setAiData({
            htmlSelectionContext,
          })
        ),
        handleAllSelected,
      ])(pair(state, nextId));
    } else {
      const subtreeForAiInsertion =
        createSubtreeForPlainTextAiInsertion(state).subtree;

      const selectionForAiInsertion =
        collapseTextSelectionAtLeft(rangeSelection);

      return pair(
        pipe([
          setAiData({
            subtreeForAiInsertion,
            selectionForAiInsertion,
            htmlSelectionContext,
          }),
        ])(state),
        nextId
      );
    }
  };

export const triggerAiGeneration =
  (
    outputFormat: 'PlainText' | 'RichText',
    plugins: Plugins.PluginsType,
    components: Component.ComponentMap,
    focusedSymbolNativeIdPath: NativeId[],
    generationEntry: AI_CONTENT_GENERATION_ENTRY
  ) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    return pipe([
      isEditingCMSOnCanvas(getFirst(stateAndId))
        ? prepContentEditorForAiGeneration(plugins, components)
        : prepContentEditorForAiGenerationWithComponentContext(
            focusedSymbolNativeIdPath,
            plugins,
            components
          ),
      idPassthrough(
        setAiData({
          isPendingRequestStartFromLlm: true,
          outputFormat,
          generationEntry,
        })
      ),
    ])(stateAndId);
  };

const insertPlainTextToSubtree =
  (responseString: string) => (state: ContentEditorActive) => {
    const rangeSelection = assertRangeSelectionRecordType(state.selection);
    if (!rangeSelection) {
      console.error('expected rangeSelection');
      return state;
    }

    return pipe([
      updateSubtree(
        insertTextAtSelection({
          newText: responseString,
          selection: rangeSelection,
        })
      ),
      // expand end of selection to end of new text
      setSelection(
        rangeSelection.set(
          'focusOffset',
          rangeSelection.focusOffset + responseString.length
        )
      ),
    ])(state);
  };

// select 1) the next element from indexFromStart (the deep text at the start of it) 2) the previous element from indexForEnd (the deep text at the end of it)
const selectStartAndEnd =
  (indexFromStart: number, indexFromEnd: number) =>
  (state: ContentEditorActive): ContentEditorActive => {
    const {subtree} = state;

    const children = getChildren(subtree);

    if (children === undefined) return state;

    const startElement = children[indexFromStart];

    if (startElement === undefined) return state;

    const startCollapsedSelection = moveCaretToDeepStartOfNode(startElement.id)(
      startElement
    );

    if (!startCollapsedSelection) return state;

    const endElement = children[children.length - (1 + indexFromEnd)];

    if (endElement === undefined) return state;

    const endCollapsedSelection = moveCaretToDeepEndOfNode(endElement.id)(
      endElement
    );

    if (!endCollapsedSelection) return state;
    return updateSelection((selection) => {
      return RangeSelectionRecord({
        anchorNodeId: startCollapsedSelection.anchorNodeId,
        anchorOffset: startCollapsedSelection.anchorOffset,
        focusNodeId: endCollapsedSelection.focusNodeId,
        focusOffset: endCollapsedSelection.focusOffset,
        leftToRight:
          assertRangeSelectionRecordType(selection)?.leftToRight ?? true,
      });
    })(state);
  };

const insertRichTextToSubtree =
  (elements: ExpressionElement[]) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const insertResult = insertElementsAfterClosestTopLevel(elements, state);
    const enforceResult = enforceTagStructure({allowLinksInContent: true})(
      pair(insertResult.state.subtree, getSecond(stateAndId))
    );
    return pair(
      pipe([
        setSubtree(getFirst(enforceResult)),
        selectStartAndEnd(
          insertResult.indexFromStart,
          insertResult.indexFromEnd
        ),
      ])(insertResult.state),
      getSecond(enforceResult)
    );
  };

export const commitGeneratedAiToSubtree =
  (generatedOption: string | ExpressionElement[]) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const {subtreeForAiInsertion, selectionForAiInsertion} = state.aiData;
    if (!subtreeForAiInsertion || !selectionForAiInsertion) return stateAndId;
    const isCmsBound = Boolean(state.item && state.field);

    return pipe([
      idPassthrough(setSubtree(subtreeForAiInsertion)),
      idPassthrough(setSelection(selectionForAiInsertion)),
      typeof generatedOption === 'string'
        ? idPassthrough(insertPlainTextToSubtree(generatedOption))
        : insertRichTextToSubtree(generatedOption),
      idPassthrough(
        setAiData({
          pendingCmsAiAttribution:
            state.aiData.pendingCmsAiAttribution || isCmsBound,
        })
      ),
      idPassthrough(setActiveToolbar(CONTENT_EDITOR_MENUS.AI_EDIT_MENU)),
    ])(stateAndId);
  };

export const handleAiGenerationSucceeded =
  (
    response: AiFieldGenerationSucceededResponse,
    components: Component.ComponentMap,
    plugins: Plugins.PluginsType
  ) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {choices, outputFormat} = response;
    const seedId = getSecond(stateAndId);
    const [generatedOptions, nextId] =
      outputFormat === 'RichText'
        ? choices.reduce(
            (acc: Pair<Array<Array<ExpressionElement>>, string>, choice) => {
              const [elements, id] = htmlToExpressionElements(
                getSecond(acc),
                choice,
                components,
                plugins
              );
              const filteredElements = elements.map((element) =>
                filterElementTree(element, isAiGenerationSupportedElement)
              );
              acc[0].push(filteredElements);
              acc[1] = id;
              return acc;
            },
            pair([], seedId)
          )
        : pair(choices, seedId);

    const firstGeneratedOption = generatedOptions[0];

    if (!firstGeneratedOption) {
      if (process.env.NODE_ENV === 'development') {
        console.error(
          'CONTENT_EDITOR_AI_FIELD_GENERATION_SUCCEEDED response was empty'
        );
      }

      return idPassthrough(
        pipe([
          setActiveToolbar(null),
          setAiData({
            isLoading: false,
          }),
        ])
      )(stateAndId);
    }

    return pipe([
      idPassthrough(
        setAiData({
          generatedOptions,
          currentOptionIndex: 0,
          isLoading: false,
        })
      ),
      commitGeneratedAiToSubtree(firstGeneratedOption),
    ])(pair(getFirst(stateAndId), nextId));
  };

export const handleAiGenerationFailed = pipe([
  setActiveToolbar(null),
  setAiData({
    isLoading: false,
  }),
]);

const memo1000 = cacheMemo(1000);

// Converts the received RTE HTML value into the Elements we're going to
// render to EvalElement by converting this HTML to Json nodes first and then
// reifying this subtree into an Element.
// NOTE: because memoize has a cache size of 1 and this is shared
// we need separate memoize caches for each node. so that in cases where multiple fields
// are parsed in a single collection item, they each get their own cache
// the max cache size/depth should be more than the number of dyn-bound elements
// rendered in the canvas on a page or rendered within a collection item in the datamanager

// We need a cache on the elementId for the dataManager where we can render multiple fields
// in a single collection item, and we want each field to cache the value separately.
// The cache depth should be the Math.max(max_rich_text_fields_in_collection, max_dynlists_on_page)
export const createRichTextElementFromValue = memo1000(
  (elementId: NativeId | string) =>
    // We need a cache on the value for the Editor-canvas to render dynamic content, because
    // each instance of the root node in the dynlist that is visible gets expanded in DynamoQuery.js
    // with a different value.
    // The cache depth should be the max_instances_within_a_dynlist_rendered_at_a_time
    memo1000((value: RichTextValue) =>
      weakMemo((componentMap: Component.ComponentMap) =>
        weakMemo((plugins: Plugins.PluginsType): ExpressionElement => {
          const dataTypes = dataTypesFromComponents(plugins, componentMap);
          const {jsonContentNodes, rootChildIds} = getRichTextValueData(
            plugins,
            dataTypes,
            value
          );

          const element = Tree.create(componentMap).getElementWithJSONNodes(
            [
              {
                _id: elementId,
                type: 'RichText',
                tag: 'div',
                children: rootChildIds,
                data: {
                  rich: true,
                  tag: 'div',
                  dyn: {
                    bind: {
                      innerHTML: {
                        mocked: true,
                      },
                    },
                  },
                  attr: {
                    tabIndex: 0,
                    'data-w-id': elementId,
                  },
                },
              },
              ...jsonContentNodes,
            ],
            plugins
          );

          if (element) {
            return element;
          } else {
            // Note this condition is just here to make Flow happy
            throw new Error('failed to reify subtree');
          }
        })
      )
    )
);

// we use this to include the larger context of where the subtree is on the page / in the component
const convertSubtreeComponentContextToHtmlForLlm = ({
  plugins,
  components,
  seedId,
  selection,
  subtree,
  root,
  allTextSelected = false,
}: {
  plugins: Plugins.PluginsType;
  components: Component.ComponentMap;
  seedId: string;
  selection: RangeSelectionRecordType;
  subtree: ExpressionElement;
  root: ExpressionElement;
  allTextSelected?: boolean;
}) => {
  const filteredSubtree = filterElementTree(
    subtree,
    isAiGenerationSupportedElement
  );
  const updatedSubtreeResult = allTextSelected
    ? wrapChildrenInStartAndStopMarkers(filteredSubtree, seedId)
    : pair(addStartAndStopMarkers(filteredSubtree, selection), seedId);
  const updatedSubtree = getFirst(updatedSubtreeResult);
  const nextId = getSecond(updatedSubtreeResult);

  const updatedRootWithSelectionTags = Expressions.updateElement(
    subtree.id,
    () => updatedSubtree,
    EElement(root)
  );

  const updatedRootWithSelectionTagsElement = Expressions.getElement(
    updatedRootWithSelectionTags
  );

  const JsonNodes = updatedRootWithSelectionTagsElement
    ? Tree.create(QualifiedMap.empty()).getElementAsJSONNodes(
        updatedRootWithSelectionTagsElement
      )
    : [];

  const dataTypes = dataTypesFromComponents(plugins, components);

  const html = rteJsonNodesToHtml(
    plugins,
    dataTypes,
    JsonNodes,
    {},
    ConversionTypeEnum.FOR_DATA_API
  );

  if (!html.includes(START) || !html.includes(STOP)) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        'failed to convert to html as stop and start selectors not found in string: ',
        html
      );
    }
    return pair(null, seedId);
  }

  return pair(
    rteJsonNodesToHtml(
      plugins,
      dataTypes,
      JsonNodes,
      {},
      ConversionTypeEnum.FOR_DATA_API
    )
      .replace(START, `<${SELECTION_AI_HTML_TAG}>`)
      .replace(STOP, `</${SELECTION_AI_HTML_TAG}>`),
    nextId
  );
};

export const convertSubtreeToHtmlForLlm = ({
  plugins,
  components,
  selection,
  subtree,
  allTextSelected = false,
  seedId,
}: {
  plugins: Plugins.PluginsType;
  components: Component.ComponentMap;
  selection: RangeSelectionRecordType;
  subtree: ExpressionElement;
  allTextSelected?: boolean;
  seedId: string;
}): Pair<string | null, string> => {
  const filteredSubtree = filterElementTree(
    subtree,
    isAiGenerationSupportedElement
  );
  const updatedSubtreeResult = allTextSelected
    ? wrapChildrenInStartAndStopMarkers(filteredSubtree, seedId)
    : pair(addStartAndStopMarkers(filteredSubtree, selection), seedId);
  const updatedSubtree = getFirst(updatedSubtreeResult);
  const nextId = getSecond(updatedSubtreeResult);

  const JsonNodes = Tree.create(QualifiedMap.empty()).getElementAsJSONNodes(
    updatedSubtree
  );

  const dataTypes = dataTypesFromComponents(plugins, components);

  const html = rteJsonNodesToHtml(
    plugins,
    dataTypes,
    JsonNodes,
    {},
    ConversionTypeEnum.FOR_DATA_API
  );

  if (!html.includes(START) || !html.includes(STOP)) {
    console.error('Unable to find string selections');
    return pair(null, seedId);
  }

  return pair(
    rteJsonNodesToHtml(
      plugins,
      dataTypes,
      JsonNodes,
      {},
      ConversionTypeEnum.FOR_DATA_API
    )
      .replace(START, `<${SELECTION_AI_HTML_TAG}>`)
      .replace(STOP, `</${SELECTION_AI_HTML_TAG}>`),
    nextId
  );
};

export const insertTextAtSelection =
  ({
    newText,
    selection,
  }: {
    newText: string;
    selection: RangeSelectionRecordType;
  }) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const updatedSubtreeEl = Expressions.updateElement(
      selection.anchorNodeId,
      (el: ExpressionElement) => {
        if (!isStringElement(el)) {
          console.error('selected element is not a string');
          return el;
        }
        const elementData = Expressions.getElementData(el);
        const currentText = Expressions.getText(elementData);

        if (currentText === null) return el;

        return Expressions.setElementData(
          el,
          EText(
            currentText.slice(0, selection.anchorOffset) +
              newText +
              currentText.slice(selection.anchorOffset)
          )
        );
      },
      EElement(subtree)
    );

    return Expressions.getElement(updatedSubtreeEl) || subtree;
  };

const attrSrcPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'src'},
] as ExpressionPath;

const updateIframeInFigure = ({
  el,
  embedUrl,
  title,
}: {
  el: ExpressionElement;
  embedUrl?: string;
  title?: string;
}): ExpressionElement => {
  const updatedElement = Expressions.everywhere(
    // @ts-expect-error TS2345 is not assignable to parameter of type (arg1: Expression) => Expression
    matchExpression({
      Element: (_el: ExpressionElement) => {
        if (isIframeElement(_el)) {
          let newExpressionData = Expressions.getElementData(_el);

          if (embedUrl !== undefined) {
            newExpressionData = Expressions.setIn(
              newExpressionData,
              attrSrcPath,
              EText(embedUrl)
            );
          }

          if (title !== undefined) {
            newExpressionData = Expressions.setIn(
              newExpressionData,
              [
                {in: 'Record', at: 'attr'},
                {in: 'Record', at: 'title'},
              ] as ExpressionPath,
              EText(title)
            );
          }
          return EElement(Expressions.setElementData(_el, newExpressionData));
        }
        return EElement(_el);
      },
      _: identity,
    })
  )(EElement(el));

  return Expressions.getElement(updatedElement) || el;
};

export const setIframeFigure =
  ({
    nodeId,
    iframeFigureData,
  }: {
    nodeId: string;
    iframeFigureData: IframeFigureData;
  }) =>
  (state: ContentEditorActive) => {
    const {srcWidth, srcHeight, url, embedUrl, title} = iframeFigureData;

    const aspectRatio =
      srcWidth && srcHeight ? getAspectRatio(srcWidth, srcHeight) : null;
    const maxHeight = aspectRatio ? String(60 / aspectRatio) + '%' : null;

    const iframeElement = Expressions.getElementById(
      EElement(state.subtree),
      nodeId
    );

    if (!iframeElement) {
      console.error('failed to find Iframe to update');
      return state;
    }

    const figureWithUpdatedIframe = updateIframeInFigure({
      el: iframeElement,
      embedUrl,
      title,
    });

    const updatedSubtree = Expressions.everywhere(
      // @ts-expect-error TS2345 is not assignable to parameter of type (arg1: Expression) => Expression
      matchExpression({
        Element: (_el: ExpressionElement) => {
          if (nodeId === _el.id) {
            if (!isFigure(_el)) {
              console.error('attempt to update non-figure element');
              return EElement(_el);
            }
            const el = figureWithUpdatedIframe;

            let newExpressionData = Expressions.getElementData(el);

            if (srcWidth !== undefined && srcHeight !== undefined) {
              if (srcWidth === 0 || srcHeight === 0) {
                newExpressionData = Expressions.setIn(
                  newExpressionData,
                  [
                    {in: 'Record', at: 'figure'},
                    {in: 'Record', at: 'dimensions'},
                  ] as ExpressionPath,
                  EText('')
                );
              }

              newExpressionData = Expressions.setIn(
                newExpressionData,
                [
                  {in: 'Record', at: 'figure'},
                  {in: 'Record', at: 'dimensions'},
                ] as ExpressionPath,
                EText(`${srcWidth}:${srcHeight}`)
              );
            }

            if (maxHeight !== null) {
              newExpressionData = Expressions.setIn(
                newExpressionData,
                [
                  {in: 'Record', at: 'figure'},
                  {in: 'Record', at: 'maxHeight'},
                ] as ExpressionPath,
                EText(maxHeight)
              );
              newExpressionData = Expressions.setIn(
                newExpressionData,
                [
                  {in: 'Record', at: 'style'},
                  {in: 'Record', at: 'paddingBottom'},
                ] as ExpressionPath,
                EText(maxHeight)
              );
            }

            if (url !== undefined) {
              newExpressionData = Expressions.setIn(
                newExpressionData,
                [
                  {in: 'Record', at: 'figure'},
                  {in: 'Record', at: 'pageUrl'},
                ] as ExpressionPath,
                EText(url)
              );
            }

            return EElement(Expressions.setElementData(el, newExpressionData));
          }

          return EElement(_el);
        },
        _: identity,
      })
    )(EElement(state.subtree));

    return setSubtree(Expressions.getElement(updatedSubtree) || state.subtree)(
      state
    );
  };

// Changes the max width of the currently selected figure node's image from the settings modal
export const figureMaxWidthUpdated = (
  value: string,
  state: ContentEditorActive
) => {
  const {subtree, selection} = state;

  const nodeId = getFigureIdFromSelection(subtree, selection);
  if (!nodeId) return state;

  // iframe figures always need width and maxwidth set.
  // image figures must only have maxWidth set
  const setWidthStyleIfIframeFigure =
    getFigureTypeById(subtree, nodeId) === 'video'
      ? setInFigureWidthStyle(value)
      : identity;

  return pipe([
    updateSubtree(
      updateElementById(
        nodeId,
        updateElementData(
          pipe([
            setInFigureMaxWidth(value),
            setInFigureMaxWidthStyle(value),
            setWidthStyleIfIframeFigure,
          ])
        )
      )
    ),
    shouldUseReactRender,
  ])(state);
};

// Changes the alignment of the currently selected figure node
export const changeFigureAlignment =
  (newAlignValue: string) => (state: ContentEditorActive) => {
    const {subtree, selection} = state;
    const nodeId = getFigureIdFromSelection(subtree, selection);
    if (!nodeId) return state;

    return pipe([
      updateSubtree(
        updateElementById(
          nodeId,
          updateElementData(setInFigureAlign(newAlignValue))
        )
      ),
      shouldUseReactRender,
    ])(state);
  };

export const resetIframeFigure = ({nodeId}: {nodeId: string}) =>
  setIframeFigure({
    nodeId,
    iframeFigureData: {
      srcWidth: 0,
      srcHeight: 0,
      url: '',
      embedUrl: '',
      title: '',
    },
  });

const htmlToExpressionElements = (
  seedId: string,
  responseString: string,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType
): Pair<Array<ExpressionElement>, string> => {
  const {jsonContentNodes, nextId} = rteHtmlToJsonNodes(responseString, {
    supportHtmlEmbeds: true,
    seedId,
  });

  return patchLineBreakElements(
    pair(reifyElementTree(jsonContentNodes, components, plugins), nextId)
  );
};

export const ensureCaretIsInATextNode = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (rangeSelection && !isCollapsed(rangeSelection)) {
    throw new Error(
      'ensureCaretIsInATextNode should only be called on a collapsed range selection'
    );
  }

  if (rangeSelection) {
    const anchorElement = Expressions.getElementById(
      EElement(subtree),
      rangeSelection.anchorNodeId
    );

    if (anchorElement && !isStringElement(anchorElement)) {
      const id = getSecond(stateAndId);

      return pair(
        pipe([
          insertNodeNextToSiblings(zwjString(id)),
          setSelection(collapseTextSelection(rangeSelection, id, 0)),
        ])(state),
        incrementGuid(id)
      );
    }
  }

  return stateAndId;
};

const handleEmptyContent =
  (shouldCreateWhitespaceString: boolean) =>
  ([
    state,
    nextId,
  ]: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {subtree} = state;
    const [idState, emptyElement] = isRichText(subtree)
      ? createParagraphWithZwj(nextId)
      : shouldCreateWhitespaceString
        ? createWhitespaceString(nextId)
        : createEmptyString({id: nextId})(state);

    return [
      setSubtree(
        Expressions.getElement(
          Expressions.setIn(
            EElement(subtree),
            [{in: 'Element'}, {in: 'Record', at: 'children'}],
            EList([EElement(emptyElement)])
          )
        ) || subtree
      )(state),
      idState.id,
    ];
  };

export const handleInitialState =
  (
    parsedSelection: ParsedSelectionType | null,
    shouldCreateWhitespaceStringIfEmpty: boolean = false
  ) =>
  (
    activeStateAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state, nextId] = activeStateAndNextId;
    const {subtree} = state;
    const children = getChildren(subtree) ?? [];

    const subtreeIsEmpty =
      children.length === 0 ||
      (!isRichText(subtree) && isEmptyTypoOrLineBreak(subtree));

    if (subtreeIsEmpty) {
      return pipe([
        handleEmptyContent(shouldCreateWhitespaceStringIfEmpty),
        idPassthrough(resetSelection),
      ])(activeStateAndNextId);
    }

    return [
      parsedSelection
        ? setSelection(reifySelectionRecord(parsedSelection))(state)
        : resetSelection(state),
      nextId,
    ];
  };

const removeCharsFromStringElement =
  (elementId: string, offset: number, numChars: number) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const element = Expressions.getElementById(EElement(subtree), elementId);
    if (!element) return subtree;

    const textValue = Expressions.getText(element.data) ?? '';

    const charToRemove = textValue.charAt(offset);

    // ZWJ_RAW characters are there to work around certain browser quirks. They're
    // added and removed in specific circumstances. We don't want to let the user
    // directly remove them via backspace/delete. UX-wise elsewhere we handle
    // skipping the caret over ZWJs when you try remove them manually, but we
    // want to make extra sure with a check here.
    if (charToRemove === ZWJ_RAW) {
      return subtree;
    } else {
      const newTextValue =
        textValue.slice(0, offset) + textValue.slice(offset + numChars);

      return updateElementData((data) =>
        Expressions.updateElement(
          element.id,
          (el) => Expressions.setElementData(el, EText(newTextValue)),
          data
        )
      )(subtree);
    }
  };

// Removes the content in a given selection and moves the caret to the proper place.
export const deleteSelectedContent = (state: ContentEditorActive) => {
  const {subtree, selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return state;

  const {leftNodeId, rightNodeId, leftOffset, rightOffset} =
    getLeftRightPosition(rangeSelection);

  const leftNode = Expressions.getElementById(subtree.data, leftNodeId);
  const rightNode = Expressions.getElementById(subtree.data, rightNodeId);

  if (!leftNode || !rightNode) return state;

  const leftIsString = isStringElement(leftNode);
  const rightIsString = isStringElement(rightNode);

  if (leftIsString && leftNodeId === rightNodeId) {
    // The selection is contained within a single text node
    const newSubtree = removeCharsFromStringElement(
      leftNodeId,
      leftOffset,
      rightOffset - leftOffset
    )(subtree);

    // collapse the caret
    const newSelection = collapseTextSelection(
      rangeSelection,
      leftNodeId,
      leftOffset
    );

    return state.set('subtree', newSubtree).set('selection', newSelection);
  }

  // Check if any of the deleted elements are components
  const deletedElements = getSelectedElements(subtree, rangeSelection);
  const removedComponentIds = deletedElements
    .filter((element) => isUserComponent(element.type))
    .map((element) => getName(element.type));

  const leftChildren = getChildren(leftNode) || [];
  const rightChildren = getChildren(rightNode) || [];

  // Note: If each edge of the range selection was in a textNode, then
  // the (left/right)Node.id and offset would point to a character. If a
  // selection edge is not in a textNode, eg between two <br>s then the
  // nodeId points to the parent of the <br> and the offset references a
  // childIndex position where that edge of the selection is, ie:
  // between two <br>s. If that happens we get the 'brace'Node which is
  // a node that's outside the edge of the selection. so that when
  // removing the nodes inside it includes the nodes within the
  // selection
  // eg: <br1> | <br2> | <br3>,  we want <br2>.
  // So the braceNodes are <br1> and <br3>
  const leftBraceNode = leftIsString
    ? leftNode
    : leftChildren[Math.max(0, leftOffset - 1)];
  const rightBraceNode = rightIsString
    ? rightNode
    : rightChildren[Math.min(rightOffset, rightChildren.length - 1)];

  const includeLeftBraceNode = !leftIsString && leftOffset === 0;
  const includeRightBraceNode = rightOffset === rightChildren.length;
  const rightText = Expressions.getText(rightNode.data) ?? '';

  // Note: If the left and right node were different textNodes then
  // they'll be merged, and the rightNodeId won't exist anymore. If the
  // left edge was between <br>s and the right side was in a textNode,
  // then there won't be a merge, but the text content that was part of
  // the selection will be erased so it should have offset of 0 and
  // collapse into the textNode (if the caret is between a <br> and
  // textNode we want it inside that textNode). Otherwise collapse left.
  const collapseRight =
    !leftIsString && rightIsString && rightText.length > rightOffset;
  const collapsedSelection = collapseRight
    ? collapseTextSelection(rangeSelection, rightNodeId, 0)
    : collapseTextSelection(rangeSelection, leftNodeId, leftOffset);

  const closestLeftFormatOrTypoNode = leftIsString
    ? Expressions.getParent(EElement(subtree), leftNode.id)
    : leftNode;
  const closestRightFormatOrTypoNode = rightIsString
    ? Expressions.getParent(EElement(subtree), rightNode.id)
    : rightNode;
  // If the left side of the selection is between a line break and the
  // left edge of the line break's parent and the right edge is in the
  // middle of a text node, then after splicing the right side's text
  // content merge the remaining nodes on the right side into the left.
  const mergeRightNodesIntoLeft = !leftIsString && rightIsString && rightText;

  return pipe([
    updateSubtree(
      pipe([
        // Note: These trimTextValue(Left/Right) methods only do things
        // if that edge of the selection is inside a textNode, otherwise
        // they're noops.
        trimTextValueRight(leftNodeId, leftOffset),
        trimTextValueLeft(rightNodeId, rightOffset),
        // This removes the nodes between the braceNodes
        leftBraceNode && rightBraceNode
          ? trimNodesInsideSelection(
              leftBraceNode.id,
              rightBraceNode.id,
              includeLeftBraceNode,
              includeRightBraceNode
            )
          : identity,
      ])
    ),

    // Note: At this point we have a filteredSubtree with all the
    // correct nodes removed. If the selection spanned multiple top-
    // level nodes (eg the anchor/focus was in two different
    // p/h1/blockquote etc.) and each edge was in a textNode then we
    // want to zip those top-level nodes up deeply

    // Note: There are some edge cases where the right node can be
    // deleted. eg: when a whole list is selected make sure the right
    // edge of the selection still exists, if it doesn't we can just
    // collapse it back to the left side.
    leftIsString && rightIsString
      ? mergeElementsSubtree(leftNodeId, rightNodeId)
      : identity,
    mergeRightNodesIntoLeft &&
    closestLeftFormatOrTypoNode &&
    closestRightFormatOrTypoNode
      ? mergeElementsSubtree(
          closestLeftFormatOrTypoNode.id,
          closestRightFormatOrTypoNode.id
        )
      : identity,
    setSelection(collapsedSelection),
    bumpSelectionEdgesIntoNearbyTextNodes,
    removedComponentIds.length > 0
      ? setShouldSyncToComponentMap(true)
      : identity,
    removedComponentIds.length > 0
      ? setRemovedComponentIds(removedComponentIds)
      : identity,
  ])(state);
};

const handleContentRemovalOnNodeSelection = (
  nodeId: string | undefined
): ((
  stateAndId: ContentEditorActiveAndNextId
) => ContentEditorActiveAndNextId) => {
  if (!nodeId) return identity;

  return pipe([
    removeTopLevelElementAndMoveCaret({
      preferredDirection: 'left',
      elementIdToRemove: nodeId,
    }),
    idPassthrough((state: ContentEditorActive) =>
      state.deleteIn(['activeUploads', nodeId])
    ),
  ]);
};

const handleBackspaceAtStartOfTopLevelTypo = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return stateAndId;
  const topLevelTypoNode = getClosestTopLevelTypoElement(
    rangeSelection.anchorNodeId,
    subtree
  );
  if (!topLevelTypoNode) return stateAndId;

  if (isEmptyTypoOrLineBreak(topLevelTypoNode))
    return removeTopLevelElementAndMoveCaret({
      preferredDirection: 'left',
      elementIdToRemove: topLevelTypoNode.id,
    })(stateAndId);

  const atLeadingEdgeOfSubtree = caretIsAtTheStartOfTheNode(subtree.id)(state);

  if (atLeadingEdgeOfSubtree) return stateAndId;

  const subtreeChildren = getChildren(subtree) || [];

  const topLevelIndex = subtreeChildren.indexOf(topLevelTypoNode);
  // At this point we know we're at the leading edge of the text node
  // and we know that it's not at the leading edge of the subtree, so there
  // is another top-level element before this one.
  const prevTopLevelSibling = subtreeChildren[topLevelIndex - 1];
  if (
    prevTopLevelSibling &&
    (isFigure(prevTopLevelSibling) ||
      isEmbed(prevTopLevelSibling) ||
      isCodeBlock(prevTopLevelSibling) ||
      isUserComponent(prevTopLevelSibling.type))
  ) {
    // If the previous sibling is a figure with a figcaption, we want to
    // move the caret to the end of the figcaption element.
    if (getFigcaptionForFigure(subtree, prevTopLevelSibling.id)) {
      const newSelection = moveCaretToDeepEndOfNode(prevTopLevelSibling.id)(
        subtree
      );

      if (!newSelection) return stateAndId;
      return pair(state.set('selection', newSelection), getSecond(stateAndId));
    }

    // Otherwise, the previous sibling is either a figure with no figcaption
    // or an embed, so we want to select the node itself.
    return pipe([
      idPassthrough(selectNode(prevTopLevelSibling.id)),
      togglePlaceholderFigcaption,
    ])(stateAndId);
  }

  // If the previous sibling was not a figure, it must be either a list
  // or a top-level typo. In those cases we want to find the deepest
  // right side text node, then its non-format ancestor and merge
  // the contents of this top-level typo into it.
  const prevTopologicalTextNode = findPreviousTopological(
    subtree,
    rangeSelection.anchorNodeId,
    isStringElement
  );
  if (!prevTopologicalTextNode) return stateAndId;
  const targetTypoNode = Expressions.closestElement(
    EElement(subtree),
    prevTopologicalTextNode.id,
    isRichTextNonFormatTypographic
  );
  if (!targetTypoNode) return stateAndId;
  // Note: deeplyJoinNodes will update the caret position for us
  return pair(
    pipe([
      deeplyJoinNodes(targetTypoNode, topLevelTypoNode, 'left'),
      shouldUseReactRender,
    ])(state),
    getSecond(stateAndId)
  );
};

export const handleDeleteAtEndOfTopLevelTypoOrFigcaption = (
  state: ContentEditorActive
) => {
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return state;
  // Note: This will either be a top-level-typo node or the figcaption
  const nonFormatTypoNode = Expressions.closestElement(
    EElement(subtree),
    rangeSelection.anchorNodeId,
    isRichTextNonFormatTypographic
  );
  // Note: This will either be a top-level-typo node or the figure node
  const topLevelNode = getClosestTopLevelElement(
    rangeSelection.anchorNodeId,
    subtree
  );
  if (!topLevelNode) return state;
  const subtreeChildren = getChildren(subtree) || [];
  const topLevelIndex = subtreeChildren.indexOf(topLevelNode);
  // At this point we know we're at the leading edge of the text node
  // and we know that it's not at the leading edge of the subtree, so there
  // is another top-level element before this one.
  const nextTopLevelSibling = subtreeChildren[topLevelIndex + 1];
  if (!nextTopLevelSibling) return state;

  if (
    isFigure(nextTopLevelSibling) ||
    isEmbed(nextTopLevelSibling) ||
    isCodeBlock(nextTopLevelSibling) ||
    isUserComponent(nextTopLevelSibling.type)
  )
    return selectNode(nextTopLevelSibling.id)(state);

  if (!nonFormatTypoNode) return state;

  // If it wasn't a figure, it must be either a list, or top-level typo. In those cases
  // we want to find the deepest left side text node of it, then it's non-format ancestor
  // and merge the contents of that non-format-ancestor into this top-level-typo
  const nextTopologicalTextNode = findNextTopological(
    subtree,
    nextTopLevelSibling.id,
    isStringElement
  );
  if (!nextTopologicalTextNode) return state;
  const targetTypoNode = Expressions.closestElement(
    EElement(subtree),
    nextTopologicalTextNode.id,
    isRichTextNonFormatTypographic
  );
  if (!targetTypoNode) return state;
  const targetParent = Expressions.getParent(
    EElement(subtree),
    targetTypoNode.id
  );
  if (!targetParent) return state;
  const shouldRemoveParent = (getChildren(targetParent) || []).length === 1;

  // Note: This is used in cases eg: with a list, where the only list item
  // was just merged into this typo leaving an empty list
  const removeEmptyParent = shouldRemoveParent
    ? removeElement(targetParent.id)
    : identity;

  // Note: deeplyJoinNodes will update the caret position for us
  return pipe([
    deeplyJoinNodes(nonFormatTypoNode, targetTypoNode, 'left'),
    removeEmptyParent,
    shouldUseReactRender,
  ])(state);
};

const handleDeleteAtEndOfListItem =
  (listItem: ExpressionElement) => (state: ContentEditorActive) => {
    const {subtree} = state;
    const listNode = Expressions.getParent(EElement(subtree), listItem.id);
    if (!listNode) return state;

    const listItems = getChildren(listNode) || [];
    const itemIndex = listItems.indexOf(listItem);

    const topLevelNodes = getChildren(subtree) || [];
    const listIndex = topLevelNodes.indexOf(listNode);

    // If it’s not last
    if (itemIndex !== listItems.length - 1) {
      const nextItem = listItems[itemIndex + 1];
      if (!nextItem) return state;
      return deeplyJoinNodes(listItem, nextItem, 'left')(state);
    }

    if (listIndex === topLevelNodes.length - 1) return state;

    const nextTopLevelNode = topLevelNodes[listIndex + 1];
    if (!nextTopLevelNode) return state;

    if (isRichTextTopLevelTypographic(nextTopLevelNode))
      return deeplyJoinNodes(listItem, nextTopLevelNode, 'left')(state);

    if (isFigure(nextTopLevelNode))
      return selectFigure(nextTopLevelNode.id)(state);

    if (isList(nextTopLevelNode)) {
      const updatedDestChildren = listItems.concat(
        getChildren(nextTopLevelNode) || []
      );

      return updateSubtree(
        updateElementData(
          pipe([
            (data) =>
              Expressions.updateElement(
                listNode.id,
                setChildren(updatedDestChildren),
                data
              ),
            (data) => removeElementById(data, nextTopLevelNode.id),
          ])
        )
      )(state);
    }

    return state;
  };

/*
1. For BACKSPACE, DELETE and CUT first handle special ux when caret is at the leading edge
of the non-format-typo node (eg: top-level-typo, list item, figcaption). Backspace behavior differs
here quite a bit from delete.
2. Otherwise, if it's at the inside/outside leading edge of a format node (eg: a, span, strong, em) handle it.
Backspace/Cut/Delete have the same UX here although in opposite directions (BACKSPACE/CUT = left, DELETE = right)
3. Otherwise, just remove a character. Again Backspace/Cut/Delete are the same except for opposite direction.
 */
export const handleContentRemovalOnCollapsedRange = (
  removalKeyOrShortcut: typeof BACKSPACE | typeof DELETE,
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const direction = removalKeyOrShortcut === BACKSPACE ? 'left' : 'right';
  const state = bumpCaretAroundZWJChar(direction)(getFirst(stateAndId));
  const nextId = getSecond(stateAndId);
  const newStateAndId = pair(state, nextId);
  const {subtree} = state;

  const isBackspace = removalKeyOrShortcut === BACKSPACE;
  const atLeadingEdgeOfNode = isBackspace
    ? caretIsAtTheStartOfTheNode
    : caretIsAtTheEndOfTheNode;

  const isEditingRichText = isRichText(subtree);

  if (!isEditingRichText && atLeadingEdgeOfNode(subtree.id)(state))
    // If the caret is at the edge of the subtree in the direction of removing content,
    // when editing simple-text then don't try anything fancy
    return stateAndId;

  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return stateAndId;

  const anchorNode = Expressions.getElementById(
    EElement(subtree),
    selection.anchorNodeId
  );

  if (!anchorNode) return stateAndId;

  const nonFormatTypoNode = Expressions.closestElement(
    EElement(subtree),
    selection.anchorNodeId,
    isRichTextNonFormatTypographic
  );

  const atInsideEdgeOfFormatNode = Boolean(
    findFurthestFormatElement(subtree.data, selection.anchorNodeId, ({id}) =>
      atLeadingEdgeOfNode(id)(state)
    )
  );

  // This looks at whether the caret is at the start of a text node
  // We'll use it in the condition block below after confirming it's not
  // at the start of the non-format-typo node. ie: if it's at the leading edge of
  // a textNode, but not the leading edge of the non-format-typo node, it must mean
  // there's at least another textNode within this non-format-typo node in the direction we're moving
  const atOutsideEdgeOfFormatNode = atLeadingEdgeOfNode(anchorNode.id)(state);

  if (
    removalKeyOrShortcut === BACKSPACE &&
    isEditingRichText &&
    nonFormatTypoNode &&
    (isEmptyTypoOrLineBreak(nonFormatTypoNode) ||
      caretIsAtTheStartOfTheNode(nonFormatTypoNode.id)(state))
  ) {
    const topLevelTypo = getClosestTopLevelTypoElement(
      selection.anchorNodeId,
      subtree
    );

    // If the caret is at the left edge of the "non format typo" eg: p, h1-6, blockquote, li, figcaption
    // then apply special ux for those node types
    if (
      isRichTextTopLevelTypographic(nonFormatTypoNode) ||
      (topLevelTypo && isEmptyTypoOrLineBreak(topLevelTypo))
    )
      return handleBackspaceAtStartOfTopLevelTypo(newStateAndId);

    if (isListItem(nonFormatTypoNode))
      return handleBackspaceAtStartOfListItem(newStateAndId);

    if (isFigCaption(nonFormatTypoNode))
      return idPassthrough(handleBackspaceAtStartOfFigcaption)(newStateAndId);
  } else if (
    removalKeyOrShortcut === DELETE &&
    isEditingRichText &&
    nonFormatTypoNode &&
    (isEmptyTypoOrLineBreak(nonFormatTypoNode) ||
      caretIsAtTheEndOfTheNode(nonFormatTypoNode.id)(state))
  ) {
    if (
      isRichTextTopLevelTypographic(nonFormatTypoNode) ||
      isFigCaption(nonFormatTypoNode)
    )
      return idPassthrough(handleDeleteAtEndOfTopLevelTypoOrFigcaption)(
        newStateAndId
      );
    if (isListItem(nonFormatTypoNode))
      return idPassthrough(handleDeleteAtEndOfListItem(nonFormatTypoNode))(
        newStateAndId
      );
  } else if (atInsideEdgeOfFormatNode || atOutsideEdgeOfFormatNode) {
    const findNearestTopologicalMethod = isBackspace
      ? findPreviousTopological
      : findNextTopological;
    const moveCaretToNearestEndMethod = isBackspace
      ? moveCaretToDeepEndOfNode
      : moveCaretToDeepStartOfNode;
    // Note: The previous if block must have been false, so it's not at the begining of a typo node,
    // therefore we know there must be another textNode or line break node within this typo node in the direction we're moving.
    const nearestTopologicalNode = findNearestTopologicalMethod(
      subtree,
      anchorNode.id,
      isStringOrLineBreak
    );
    if (!nearestTopologicalNode) return stateAndId;
    if (isLineBreakElement(nearestTopologicalNode)) {
      // Just in case we're removing a <br> that's the only child of it's parent
      // we want to make sure to clean up those empty nodes all the way up.
      const furthestEmptyFormatNode =
        findHighestEmptyFormatAncestor(subtree, nearestTopologicalNode) ||
        nearestTopologicalNode;
      if (!furthestEmptyFormatNode) return stateAndId;

      // After removing the line break we want to make sure to try merge its
      // left and right sibling if it had them
      const parentNode = Expressions.getParent(
        EElement(subtree),
        furthestEmptyFormatNode.id
      );
      const siblings = (parentNode && getChildren(parentNode)) || [];
      const childIndex = findIndexById(furthestEmptyFormatNode.id, siblings);
      const leftSibling = siblings[childIndex - 1];
      const rightSibling = siblings[childIndex + 1];
      const mergeRemainingSiblingsIfNeeded =
        leftSibling && rightSibling
          ? deeplyJoinNodes(leftSibling, rightSibling, 'left')
          : identity;

      // If the nearest topological node is a line break, we just remove that
      // node from the subtree
      return idPassthrough(
        pipe([
          removeElement(furthestEmptyFormatNode.id),
          mergeRemainingSiblingsIfNeeded,
          shouldUseReactRender,
        ])
      )(newStateAndId);
    }

    // If the nearest topological node is a text node, we want first to update the selection by first moving
    // it to the next anchor id and then handling the delete/backspace key to remove the character
    return pipe([
      idPassthrough(
        setSelection(
          moveCaretToNearestEndMethod(nearestTopologicalNode.id)(subtree) ||
            selection
        )
      ),
      handleBackspaceDeleteKeyInTextNode(removalKeyOrShortcut),
      idPassthrough(shouldUseReactRender),
    ])(newStateAndId);
  } else if (!isStringElement(anchorNode)) {
    // If we have a range selection and the caret is not inside a textNode, it's
    // likely between two <br>s. In some cases the two <br>s may be at different
    // nesting depths. We want to put the caret at the depth next to the <br>
    // we're about to delete first.
    const delta = removalKeyOrShortcut === DELETE ? 0 : -1;
    const fromIndex = selection.anchorOffset + delta;
    const siblingWithNodeToRemove = (getChildren(anchorNode) || [])[fromIndex];
    if (!siblingWithNodeToRemove) return stateAndId;
    const findNearestTopologicalMethod = isBackspace
      ? getRightDeepestTextOrLineBreak
      : getLeftDeepestTextOrLineBreak;
    const nearestTopologicalLineBreak = findNearestTopologicalMethod(
      subtree,
      siblingWithNodeToRemove.id
    );
    if (!nearestTopologicalLineBreak) return stateAndId;
    const nodeToRemove =
      findHighestEmptyFormatAncestor(subtree, nearestTopologicalLineBreak) ||
      nearestTopologicalLineBreak;
    const parentNode = Expressions.getParent(
      EElement(subtree),
      nodeToRemove.id
    );
    if (!parentNode) return stateAndId;
    const siblings = getChildren(parentNode) || [];
    const childIndex = findIndexById(nodeToRemove.id, siblings);
    const newCaretIndex =
      removalKeyOrShortcut === BACKSPACE ? childIndex + 1 : childIndex;
    const preferredDirection =
      removalKeyOrShortcut === BACKSPACE ? 'left' : 'right';
    const leftSibling = siblings[childIndex - 1];
    const rightSibling = siblings[childIndex + 1];
    const shouldMergeSiblings =
      leftSibling && rightSibling && shouldMerge(leftSibling, rightSibling);
    const mergeSiblingsIfNeeded = shouldMergeSiblings
      ? deeplyJoinNodes(leftSibling, rightSibling, 'left')
      : identity;
    return idPassthrough(
      pipe([
        // put the caret at the correct depth next to the <br> we're removing
        setSelection(
          collapseTextSelection(selection, parentNode.id, newCaretIndex)
        ),
        // remove it
        removeElement(nodeToRemove.id),
        // handle the caret now that it's been removed
        moveToFormatNodeSibling(
          preferredDirection,
          parentNode.id,
          newCaretIndex + delta
        ),
        mergeSiblingsIfNeeded,
      ])
    )(newStateAndId);
  }

  // otherwise just remove a character
  return pipe([
    handleBackspaceDeleteKeyInTextNode(removalKeyOrShortcut),
    idPassthrough(shouldUseReactRender),
  ])(newStateAndId);
};

const handleEmptyTypoOrListNodeAfterContentRemoval =
  (
    removalKeyOrShortcut:
      | typeof BACKSPACE
      | typeof DELETE
      | typeof SHORTCUTS.CUT,
    removedElements: Array<ExpressionElement>
  ) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {selection, subtree} = getFirst(stateAndId);

    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (!rangeSelection) return stateAndId;

    const listNode = getClosestListElement(
      EElement(subtree),
      rangeSelection.anchorNodeId
    );
    const numberOfTypoAndListRemovedNodes = removedElements.filter(
      (element) => isRichTextTopLevelTypographic(element) || isList(element)
    ).length;
    const numberOfListItems = removedElements.filter(isListItem).length;

    if (
      listNode &&
      numberOfTypoAndListRemovedNodes === 1 &&
      numberOfListItems > 1
    ) {
      const anchorNode = Expressions.getElementById(
        EElement(subtree),
        rangeSelection.anchorNodeId
      );

      if (!anchorNode) return stateAndId;

      if (
        !isStringElement(anchorNode) &&
        (getChildren(anchorNode) || []).length === 0
      )
        // If the left edge of the selection was in front of a line break the
        // anchorNode id references the parent of the line break. If everything in
        // the node was selected before deleting that typonode is now empty. So
        // we need to add a ZWJ textNode and put the caret inside
        return addEmptyStringElement(anchorNode)(stateAndId);

      return stateAndId;
    }

    const typoNode = getClosestTopLevelTypoElement(
      rangeSelection.anchorNodeId,
      subtree
    );
    const parent = typoNode || listNode;

    if (!parent || !isEmptyTypoOrLineBreak(parent)) return stateAndId;

    const isBackspaceOrShortcut = removalKeyOrShortcut === BACKSPACE;

    const getSibling = isBackspaceOrShortcut ? getNext : getPrev;
    const sibling = maybe<null | ExpressionElement>(null)(
      (nextSibling: ExpressionElement) =>
        isRichTextTopLevelTypographic(nextSibling) || isList(nextSibling)
          ? nextSibling
          : null
    )(getSibling(parent.id)(EElement(subtree)));

    if (sibling && numberOfTypoAndListRemovedNodes === 1) {
      const moveCaretToTheNearestSibling = isBackspaceOrShortcut
        ? moveCaretToDeepStartOfNode
        : moveCaretToDeepEndOfNode;
      return idPassthrough(
        pipe([
          removeElement(parent.id),
          setSelection(
            moveCaretToTheNearestSibling(sibling.id)(subtree) || rangeSelection
          ),
        ])
      )(stateAndId);
    }

    return pipe([
      addParagraphWithEmptyString(parent.id, 'after'),
      idPassthrough(removeElement(parent.id)),
    ])(stateAndId);
  };

export const handleContentRemoval =
  (
    removalKeyOrShortcut:
      | typeof BACKSPACE
      | typeof DELETE
      | typeof SHORTCUTS.CUT
  ) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {subtree, selection} = getFirst(stateAndId);

    if (selection.type === 'Node')
      return handleContentRemovalOnNodeSelection(selection.nodeId)(stateAndId);

    if (isCollapsed(selection) && removalKeyOrShortcut !== SHORTCUTS.CUT)
      return handleContentRemovalOnCollapsedRange(
        removalKeyOrShortcut,
        stateAndId
      );

    const removedElements = getSelectedElements(subtree, selection);

    return pipe([
      idPassthrough(deleteSelectedContent),
      isRichText(subtree)
        ? handleEmptyTypoOrListNodeAfterContentRemoval(
            removalKeyOrShortcut,
            removedElements
          )
        : identity,
      handleEmptyTextNodeAfterContentRemoval(removalKeyOrShortcut),
    ])(stateAndId);
  };

export const selectionContainsComponent = (
  state: ContentEditorActive
): boolean => {
  const {subtree, selection} = state;
  const nodeSelection = assertNodeSelectionRecordType(selection);
  if (nodeSelection) {
    const node = Expressions.getElementById(
      EElement(subtree),
      nodeSelection?.nodeId || ''
    );
    return node ? isUserComponent(node.type) : false;
  }

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return false;

  const selectedElements = getSelectedElements(subtree, rangeSelection);

  return selectedElements.some((el) => isUserComponent(el.type));
};

/**
 * Guards component operations (delete, cut, paste, key presses) inside the
 * Content Editor. Used by ContentEditorStore to early-return from actions that
 * would modify a component, and by NotificationStore to decide whether to show
 * a "switch locales" toast.
 *
 * Rules:
 * - Edit mode (Content Editor role) users are always blocked from component
 *   operations, regardless of locale or rich text type.
 * - Users on a page branch are always blocked from add/paste in dynamic
 *   (bound) rich text, regardless of locale. CMS content isn't branched, but
 *   components are, so branch-only components could leak into (or be
 *   referenced from) the base site's CMS content.
 * - Design/build mode users on a secondary locale in dynamic (bound) rich text
 *   are allowed to delete components. But not in static rich text.
 * - All other cases defer to the `canAddComponentsInRichText` ability, which
 *   grants permission to design/build mode users on the primary locale when
 *   the feature flags are enabled.
 */
export const shouldBlockComponentOperation = (
  designerStores: DesignerStores,
  activeState: ContentEditorActive
): boolean => {
  // If the selection does not contain a component, don't block the operation.
  if (!selectionContainsComponent(activeState)) {
    return false;
  }

  // If the user is in edit mode, block the operation.
  if (selectIsEditMode(designerStores)) {
    return true;
  }

  // If the user is editing a bound rich text element on a page branch, block
  // the operation. CMS content isn't branched, but components are, so
  // adding/pasting components into dynamic RTF on a branch is unsafe.
  if (isEditingDynamicCMSContent(activeState) && activeState.isPageBranch) {
    return true;
  }

  // If the user is editing a bound rich text element on a secondary locale,
  // don't block the operation.
  if (
    isEditingDynamicCMSContent(activeState) &&
    !activeState.isOnPrimaryLocale
  ) {
    return false;
  }

  // Otherwise, check if the user has permission to add components.
  // If they don't, block the operation.
  return !selectDesignerAbility('canAddComponentsInRichText')(designerStores);
};

/**
 * Guards whether a NEW component pasted from the clipboard should be
 * stripped out before it's inserted, in `ContentEditor.tsx`'s paste handler.
 *
 * This is a separate concern from `shouldBlockComponentOperation`, which
 * guards *replacing* an already-selected component via paste. The two can
 * overlap when the clipboard has a component AND the current selection is
 * also a component (e.g. pasting one component over another).
 *
 * Rules:
 * - Edit mode and secondary locale always strip, regardless of the current
 *   selection — this is what prevents a new component from sneaking in via
 *   `shouldBlockComponentOperation`'s deliberate secondary-locale allowance
 *   (which exists to permit replacing/deleting a component via paste, not
 *   adding a new one).
 * - A page branch also strips, but only skips this guard entirely
 *   (deferring to `shouldBlockComponentOperation`) when the selection
 *   already contains a component — in that case the whole paste gets
 *   blocked (and toasted) there regardless of clipboard content, so
 *   stripping here would just be redundant and risk a second toast.
 */
export const shouldStripPastedComponents = (
  activeState: ContentEditorActive,
  isEditMode: boolean
): boolean => {
  const isBranchBlocked =
    isEditingDynamicCMSContent(activeState) && activeState.isPageBranch;

  if (isBranchBlocked && selectionContainsComponent(activeState)) {
    return false;
  }

  return !activeState.isOnPrimaryLocale || isEditMode || isBranchBlocked;
};

/**
 * Guards component *deletion* (delete, backspace, cut, and the
 * replace-by-typing operations: space, enter, character insertion over a
 * selected component) inside the Content Editor.
 *
 * Unlike `shouldBlockComponentOperation` — which guards add/paste and stays
 * restricted to the primary locale for static rich text — deletion is allowed
 * on secondary locales for both static and dynamic rich text. The only
 * remaining restrictions are edit mode and, for dynamic rich text, page
 * branches.
 *
 * Rules:
 * - Edit mode (Content Editor role) users are always blocked from deleting
 *   components, regardless of locale or rich text type.
 * - Users on a page branch are always blocked from deleting components in
 *   dynamic (bound) rich text, regardless of locale. But not in static rich
 *   text.
 * - Design/build mode users may delete components on any locale, in both
 *   static and dynamic rich text.
 */
export const shouldBlockComponentDeletion = (
  designerStores: DesignerStores,
  activeState: ContentEditorActive
): boolean => {
  if (!selectionContainsComponent(activeState)) {
    return false;
  }

  // Edit mode users are blocked from deleting components in rich text.
  if (selectIsEditMode(designerStores)) {
    return true;
  }

  // Users on a page branch are blocked from deleting components in dynamic
  // (bound) rich text, since CMS content isn't branched but components are.
  return isEditingDynamicCMSContent(activeState) && activeState.isPageBranch;
};

export const trimTextValueRight =
  (elementId: string, offset: number) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const element = Expressions.getElementById(EElement(subtree), elementId);
    if (!element) return subtree;

    if (!isStringElement(element)) {
      return subtree;
    }

    const textValue = Expressions.getText(element.data) ?? '';
    const newTextValue = textValue.slice(0, offset);

    return updateElementData((data) =>
      Expressions.updateElement(
        element.id,
        (el) => Expressions.setElementData(el, EText(newTextValue)),
        data
      )
    )(subtree);
  };

export const trimTextValueLeft =
  (elementId: string, offset: number) =>
  (subtree: ExpressionElement): ExpressionElement => {
    const element = Expressions.getElementById(EElement(subtree), elementId);
    if (!element) return subtree;

    if (!isStringElement(element)) {
      return subtree;
    }

    const textValue = Expressions.getText(element.data) ?? '';
    const newTextValue = textValue.slice(offset);

    return updateElementData((data) =>
      Expressions.updateElement(
        element.id,
        (el) => Expressions.setElementData(el, EText(newTextValue)),
        data
      )
    )(subtree);
  };

export const removeCharToLeft = (state: ContentEditorActive) => {
  const {selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return state;
  if (!isCollapsed(rangeSelection)) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        'removeCharToLeft should only be called on a collapsed selection'
      );
    }
    return state;
  }

  const charToRemoveOffset = rangeSelection.anchorOffset - 1;

  return pipe([
    changeCaretOffsetBy(-1),
    updateSubtree(
      removeCharsFromStringElement(
        rangeSelection.anchorNodeId,
        charToRemoveOffset,
        1
      )
    ),
  ])(state);
};

export const removeCharToRight = (state: ContentEditorActive) => {
  const {selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return state;
  if (!isCollapsed(rangeSelection)) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        'removeCharToRight should only be called on a collapsed selection'
      );
    }
    return state;
  }

  const charToRemoveOffset = rangeSelection.anchorOffset;

  return updateSubtree(
    removeCharsFromStringElement(
      rangeSelection.anchorNodeId,
      charToRemoveOffset,
      1
    )
  )(state);
};

const prepSelectionForPaste = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {selection} = getFirst(stateAndId);

  // For consistency we make sure we always have a range selection when inserting characters
  // if there's a node selection, add a paragraph after and insert the nodes into that new paragraph
  if (selection.type === 'Node' && selection.nodeId)
    // a figure is selected, add a new paragraph after it with the caret inside
    return addParagraphWithEmptyString(selection.nodeId, 'after')(stateAndId);

  if (selection.type !== 'Range' || !isCollapsed(selection))
    return idPassthrough(deleteSelectedContent)(stateAndId);

  return stateAndId;
};

const splitTextNodeAtCurrentAnchor = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const selection = assertRangeSelectionRecordType(state.selection);
  if (!selection) return stateAndId;

  const textNodeId = selection.anchorNodeId;
  const textElement = Expressions.getElementById(
    EElement(state.subtree),
    textNodeId
  );

  if (!textElement) return stateAndId;

  const currentTextValue = Expressions.getText(textElement.data) || '';
  const leftText = currentTextValue.slice(0, selection.anchorOffset);
  const rightText = currentTextValue.slice(selection.anchorOffset);

  const rightId = getSecond(stateAndId);
  const rightEl = {
    id: rightId,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText(rightText),
  };

  return pair(
    updateSubtree(
      pipe([
        setStringValue(textNodeId, leftText),
        insertElement(rightEl, textNodeId, 'after'),
      ])
    )(state),
    incrementGuid(rightId)
  );
};

const insertNodes =
  (nodesToInsert: Array<ExpressionElement>) => (state: ContentEditorActive) => {
    const anchor = assertRangeSelectionRecordType(
      state.selection
    )?.anchorNodeId;
    if (!anchor) return state;
    const subtree = nodesToInsert.reduceRight(
      (acc, el) => insertElement(el, anchor, 'after')(acc),
      state.subtree
    );
    return setSubtree(subtree)(state);
  };

export const fixSelectionAfterSelectAllDeleteAndPaste = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree, selection} = state;
  const children = getChildren(subtree);
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (
    rangeSelection &&
    rangeSelection.anchorNodeId === subtree.id &&
    children &&
    children.length > 0
  ) {
    if (getAllTextInElement(subtree).length > 0) return stateAndId;

    const id = getSecond(stateAndId);
    const [updatedChildrenEl, nextId] = isRichText(subtree)
      ? createParagraphWithText('', id)
      : createStringElement('', id);

    return pair(
      pipe([
        updateSubtree(updateChildren(subtree.id, (_) => [updatedChildrenEl])),
        selectStartOfString(id),
      ])(state),
      nextId
    );
  }

  return stateAndId;
};

// This will insert a list of ContentEditor-compatible elements
// at the current caret position, it will then raise each one to the proper hierarchy
// and merge with adjacent content as needed.
export const insertPastedNodes =
  (nodesToInsert: Array<ExpressionElement>) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const insertedNodeIds = new Set(nodesToInsert.map(({id}) => id));
    const opts = {
      allowLinksInContent: canHaveLinksInContent(getFirst(stateAndId)),
    };

    return pipe<ContentEditorActiveAndNextId>([
      // Fixes a crash that occurs when a user selects all, deletes, and then pastes in the Content Editor.
      fixSelectionAfterSelectAllDeleteAndPaste,
      // make pasting easier by ensuring there's always a RangeSelection
      prepSelectionForPaste,
      // We want to move the caret to the end of the new nodes.
      // Because nodes can be merged, removed, change position, etc. tracking it by id would only work
      // if the tag enforcement steps managed the selection/caret position as it moved nodes around.
      // We want to eventually run tag enforcement on the server, and in contexts where
      // there is a subtree without a selection, so we can decouple the selection tracking by inserting a unicode character
      // before and then after tag enforcement replacing that character with the caret.
      idPassthrough(insertChars(PASTE_SELECTION_MARKER)),
      idPassthrough(changeCaretOffsetBy(-1)),
      splitTextNodeAtCurrentAnchor,
      idPassthrough(insertNodes(nodesToInsert)),
      ([state, id]) => {
        const scopedRange = isRichText(state.subtree)
          ? getScopedTopLevelRangeForPaste(state.subtree, insertedNodeIds)
          : null;
        const [subtree, nextId] = scopedRange
          ? enforceTagStructureScoped(opts)(
              pair(state.subtree, id),
              scopedRange
            )
          : enforceTagStructure(opts)(pair(state.subtree, id));
        return pair(setSubtree(subtree)(state), nextId);
      },
      idPassthrough(restoreSelectionAfterPaste),
      handleEmptyTextNodeAfterContentRemoval(BACKSPACE),
    ])(stateAndId);
  };

const indexOfPasteSelectionMarker = (el: ExpressionElement) =>
  (Expressions.getText(el.data) || '').indexOf(PASTE_SELECTION_MARKER);

const getScopedTopLevelRangeForPaste = (
  subtree: ExpressionElement,
  insertedNodeIds: ReadonlySet<string>
): TopLevelRichTextRange | null => {
  const children = getChildren(subtree) ?? emptyArray;
  if (children.length === 0) return null;

  const topLevelIds = new Set<string>();

  for (const insertedNodeId of insertedNodeIds) {
    const topLevelElement = getClosestTopLevelElement(insertedNodeId, subtree);
    if (topLevelElement) {
      topLevelIds.add(topLevelElement.id);
    }
  }

  const markerNode = findElementDescendant(
    subtree,
    (el) => indexOfPasteSelectionMarker(el) > -1
  );
  if (markerNode) {
    const markerTopLevelElement = getClosestTopLevelElement(
      markerNode.id,
      subtree
    );
    if (markerTopLevelElement) {
      topLevelIds.add(markerTopLevelElement.id);
    }
  }

  if (topLevelIds.size === 0) return null;

  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  children.forEach((child, index) => {
    if (topLevelIds.has(child.id)) {
      start = Math.min(start, index);
      end = Math.max(end, index);
    }
  });

  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  // Keep one block of context on each side for boundary merges.
  const lastIndex = children.length - 1;
  return [clamp(0, lastIndex, start - 1), clamp(0, lastIndex, end + 1)];
};

export const restoreSelectionAfterPaste = (state: ContentEditorActive) => {
  const rangeSelection = assertRangeSelectionRecordType(state.selection);
  if (rangeSelection === null) return state;

  const textNode = findElementDescendant(
    state.subtree,
    (el) => indexOfPasteSelectionMarker(el) > -1
  );

  if (!textNode) return state;

  // restore caret after inserting and merging nodes
  const offset = indexOfPasteSelectionMarker(textNode);

  // RemoveCharToTheLeft will erase the paste marker, and add a ZWJ_RAW if it was the last
  // character in the textNode.
  return pipe([
    setSelection(
      collapseTextSelection(rangeSelection, textNode.id, offset + 1)
    ),
    removeCharToLeft,
  ])(state);
};

// Scans up the tree to find the highest format node that would be empty
// if the given node were to be deleted
export const findHighestEmptyFormatAncestor = (
  subtree: ExpressionElement,
  element: ExpressionElement
): ExpressionElement | null => {
  const parent = Expressions.getParent(EElement(subtree), element.id);

  if (parent === null) return null;

  const children = getChildren(parent) ?? [];
  if (children.length === 1) {
    if (parent !== subtree && isFormatElement(parent)) {
      return findHighestEmptyFormatAncestor(subtree, parent);
    }
  }

  return isFormatElement(element) ? element : null;
};

const updateFormatToggles =
  (bold: boolean, italic: boolean) => (state: ContentEditorActive) =>
    state.update('formatToggle', (toggles) =>
      toggles.set('bold', bold).set('italic', italic)
    );

const isStrongOrEmphasized = (element: ExpressionElement) =>
  isStrong(element) || isEmphasized(element);

const setFormatToggles =
  (highestEmptyFormatAncestor: ExpressionElement | null) =>
  (state: ContentEditorActive) => {
    if (highestEmptyFormatAncestor === null) return state;

    const elementsToRemovedFlattened = findAllDescendantsFromExpression(
      EElement(highestEmptyFormatAncestor),
      isStrongOrEmphasized
    );

    if (elementsToRemovedFlattened.length === 0) return state;

    const toggleBold = Boolean(elementsToRemovedFlattened.find(isStrong));
    const toggleItalic = Boolean(elementsToRemovedFlattened.find(isEmphasized));

    return updateFormatToggles(toggleBold, toggleItalic)(state);
  };

const insertLinebreakAtOffset =
  (id: string, offset: number) =>
  (children: ExpressionElement[]): ExpressionElement[] => {
    const lineBreakElement = {
      id,
      type: qualify(Plugins.name('Basic'), Component.name('LineBreak')),
      data: ERecord({}),
    };

    return [
      ...children.slice(0, offset),
      lineBreakElement,
      ...children.slice(offset),
    ];
  };
export const insertLineBreak = (
  stateAndNextId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndNextId);
  const seedId = getSecond(stateAndNextId);
  const {subtree, selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);

  if (!rangeSelection || !isCollapsed(rangeSelection)) return stateAndNextId;

  const caretIsAtStart = caretIsAtStartOfTextNode(rangeSelection)(state);

  const brResult = createLineBreak(seedId);
  const br = getSecond(brResult);

  if (caretIsAtStart) {
    const containerNode = Expressions.getParent(
      EElement(subtree),
      rangeSelection.anchorNodeId
    );

    if (!containerNode) return stateAndNextId;

    const containerChildren = getChildren(containerNode) || [];
    const index = findIndexById(rangeSelection.anchorNodeId, containerChildren);
    const prevSibling = containerChildren[index - 1];

    if (index === 0 || (prevSibling && isLineBreakElement(prevSibling))) {
      return pair(
        updateSubtree(
          updateElementData((data) =>
            addElementAtAnchorById(
              data,
              br,
              rangeSelection.anchorNodeId,
              'before'
            )
          )
        )(state),
        getFirst(brResult).id
      );
    }
    return stateAndNextId;
  }

  const anchorNode = Expressions.getElementById(
    subtree.data,
    rangeSelection.anchorNodeId
  );

  if (!anchorNode) return stateAndNextId;

  if (!isStringElement(anchorNode)) {
    // The caret is both collapsed and not in a text node so we assume it is next to a <br> in the textholder element.
    // just add the <br> after the current position.
    return [
      pipe([
        updateSubtree(
          updateChildren(
            anchorNode.id,
            insertLinebreakAtOffset(seedId, rangeSelection.anchorOffset)
          )
        ),
        changeCaretOffsetBy(1),
      ])(state),
      incrementGuid(seedId),
    ];
  }

  const currentTextValue = Expressions.getText(anchorNode.data) || '';
  const leftTextNodeValue = currentTextValue.substring(
    0,
    rangeSelection.anchorOffset
  );
  const rightTextNodeValue = currentTextValue.substring(
    rangeSelection.anchorOffset
  );
  const rightId = getFirst(brResult).id;
  // add right text node
  const rightText = {
    id: rightId,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText(rightTextNodeValue ? rightTextNodeValue : ZWJ_RAW),
  };

  return pair(
    pipe([
      updateSubtree(
        updateParentChildren(rangeSelection.anchorNodeId, (children) => {
          const index = findIndexById(rangeSelection.anchorNodeId, children);
          return [
            ...children.slice(0, index),
            {...anchorNode, data: EText(leftTextNodeValue)},
            br,
            rightText,
            ...children.slice(index + 1),
          ];
        })
      ),
      selectStartOfString(rightText.id),
    ])(state),
    incrementGuid(rightId)
  );
};

export const handleBrShortcut = pipe([
  idPassthrough(shouldUseReactRender),
  idPassthrough(deleteSelectedContent),
  insertLineBreak,
]);

export const insertCharacters = (chars: string) =>
  pipe([
    idPassthrough(setConsecutiveSpacebarAttempts(0)),
    idPassthrough(shouldUseReactRender),
    idPassthrough(deleteSelectedContent),
    ensureCaretIsInATextNode,
    // If there was a pending format. ie: the user pressed cmd+b/cmd+i on a
    // collapsed caret, create/remove the requested format node nesting
    // before inserting the characters
    applyPendingFormat,
    idPassthrough(insertChars(chars)),
  ]);

export const handleSpaceKeyOnKeyDown =
  (attempts: number) => (state: ContentEditorActive) => {
    const {selection, consecutiveSpacebarAttempts} = state;
    const rangedSelection = assertRangeSelectionRecordType(selection);
    const wasCollapsed = rangedSelection ? isCollapsed(rangedSelection) : false;
    const isBlocked = attempts !== consecutiveSpacebarAttempts;

    // If the space was blocked increment the blocked notification counter
    if (isBlocked) {
      const shouldKeepNativeRender =
        wasCollapsed && shouldKeepNativeRenderForTyping(state);

      return pipe([
        deleteSelectedContent,
        setConsecutiveSpacebarAttempts(attempts),
        setNativeRender(shouldKeepNativeRender),
      ])(state);
    }

    return state;
    // Note: If it was an allowed regular space, we want onBeforeInput to handle it
  };

/** Adding a line break before a nested list ensures that the text can be added before the list. If this isn't done, the list item collapse and prevents the caret entering the space between where the list items opens and the nested list.
 / <ul>
 / ...
 /   <li>
 /     '' <-- we need this
 /     <br>
 /      <ul/>
 / ...
 */
const insertLineBreakForNestedList =
  (ancestorNode: ExpressionElement) =>
  (
    contentEditorActiveAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state, nextId] = contentEditorActiveAndNextId;
    const {subtree} = state;

    if (isListItem(ancestorNode)) {
      const children = getChildren(ancestorNode) ?? [];
      const firstChild = children[0];
      const secondChild = children[1];

      if (!firstChild || !secondChild) return contentEditorActiveAndNextId;

      const firstChildIsEmptyText = getTextValue(firstChild) === '';
      const secondIsList = isList(secondChild);
      if (firstChildIsEmptyText && secondIsList) {
        const [uniqueIdState, lineBreak] = createLineBreak(nextId);

        return [
          setSubtree(
            Expressions.getElement(
              addElementAtAnchor(
                EElement(subtree),
                lineBreak,
                firstChild,
                'after'
              )
            ) ?? subtree
          )(state),
          uniqueIdState.id,
        ];
      }
    }
    return contentEditorActiveAndNextId;
  };

const moveCaretToTextNode =
  (
    nearestTargetNode: ExpressionElement,
    nearestEndMethod: (
      elementId: string
    ) => (subtreElement: ExpressionElement) => RangeSelectionRecordType | null
  ) =>
  (state: ContentEditorActive) => {
    const {subtree} = state;
    const updatedSelection = nearestEndMethod(nearestTargetNode.id)(subtree);

    return updatedSelection ? state.set('selection', updatedSelection) : state;
  };

const moveCaretToLineBreak = (
  nearestTargetNode: ExpressionElement,
  rangeSelection: RangeSelectionRecordType,
  isMovingLeft: boolean,
  state: ContentEditorActive
) => {
  const {subtree} = state;
  const parent = Expressions.getParent(EElement(subtree), nearestTargetNode.id);

  if (!parent) return state;

  const lineBreakIndex = findIndexById(
    nearestTargetNode.id,
    getChildren(parent) ?? []
  );

  const index = isMovingLeft ? lineBreakIndex + 1 : lineBreakIndex;
  const updatedSelection = rangeSelection
    ? collapseTextSelection(rangeSelection, parent.id, index)
    : state.selection;
  return state.set('selection', updatedSelection);
};

const addEmptyStringElement =
  (ancestorNode: ExpressionElement) =>
  (
    contentEditorActiveAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state, nextId] = contentEditorActiveAndNextId;
    const {subtree} = state;
    const [uniqueIdState, emptyTextElement] = createEmptyString({id: nextId})(
      state
    );

    const updatedSubtree = Expressions.getElement(
      addElementAtAnchor(
        EElement(subtree),
        emptyTextElement,
        ancestorNode,
        'append'
      )
    );

    if (!updatedSubtree) return contentEditorActiveAndNextId;

    const updatedSelection = moveCaretToDeepStartOfNode(emptyTextElement.id)(
      updatedSubtree
    );

    return [
      state
        .set('subtree', updatedSubtree)
        .set('selection', updatedSelection || state.selection),
      uniqueIdState.id,
    ];
  };

// Try:
// 1) placing the caret in the nearest String element (or next to the
// nearest line break) in the preferred direction
// 2) If there isn't an String / line break to move into then try opposite direction.
//
//  fall back to creating a new 'empty' text node with a ZWJ character inside and move into that
const updateCaretPosition =
  ({
    nearestTargetNode,
    nearestEndMethod,
    rangeSelection,
    ancestorNode,
    isMovingLeft,
  }: {
    nearestTargetNode: ExpressionElement | null;
    nearestEndMethod: (
      elementId: string
    ) => (subtreElement: ExpressionElement) => RangeSelectionRecordType | null;
    rangeSelection: RangeSelectionRecordType;
    ancestorNode: ExpressionElement;
    isMovingLeft: boolean;
  }) =>
  (
    contentEditorActiveAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state, nextId] = contentEditorActiveAndNextId;

    if (nearestTargetNode) {
      if (isStringElement(nearestTargetNode)) {
        return [
          pipe([
            moveCaretToTextNode(nearestTargetNode, nearestEndMethod),
            mergeWithAdjacentSiblings(nearestTargetNode.id),
          ])(state),
          nextId,
        ];
      } else {
        return [
          moveCaretToLineBreak(
            nearestTargetNode,
            rangeSelection,
            isMovingLeft,
            state
          ),
          nextId,
        ];
      }
    } else {
      return addEmptyStringElement(ancestorNode)(contentEditorActiveAndNextId);
    }
  };

const findNextTopological = (
  subtree: ExpressionElement,
  elementId: string,
  predicate: (el: ExpressionElement) => boolean
): ExpressionElement | null => {
  let isAfterElement = false;
  let hasFoundElementAfterElementId = false;

  return (
    findAllDescendantsFromExpression(EElement(subtree), (e) => {
      if (hasFoundElementAfterElementId) return false;
      if (e.id === elementId) {
        isAfterElement = true;
        return false;
      }
      if (!isAfterElement) return false;

      hasFoundElementAfterElementId = predicate(e);

      return hasFoundElementAfterElementId;
    })[0] ?? null
  );
};

const findPreviousTopological = (
  subtree: ExpressionElement,
  elementId: string,
  predicate: (el: ExpressionElement) => boolean
): ExpressionElement | null => {
  let isBeforeElement = true;

  const MatchingElementsBeforeElementId = findAllDescendantsFromExpression(
    EElement(subtree),
    (e) => {
      if (!isBeforeElement) return false;
      if (e.id === elementId) {
        isBeforeElement = false;
        return false;
      }
      return predicate(e);
    }
  );
  return (
    MatchingElementsBeforeElementId[
      MatchingElementsBeforeElementId.length - 1
    ] ?? null
  );
};

const isTextOrLineBreak = (el: ExpressionElement): boolean =>
  isStringElement(el) || isLineBreakElement(el);

export const findPreviousTextOrLineBreak = (
  subtree: ExpressionElement,
  elementId: string
): ExpressionElement | null => {
  return findPreviousTopological(subtree, elementId, isTextOrLineBreak);
};

export const findNextTextOrLineBreak = (
  subtree: ExpressionElement,
  elementId: string
): ExpressionElement | null => {
  return findNextTopological(subtree, elementId, isTextOrLineBreak);
};

const setZwj = updateElementData((_) => EText(ZWJ_RAW));

export const addZwjToStringElement = (elId: string) =>
  updateSubtree(
    updateElementData((data) => Expressions.updateElement(elId, setZwj, data))
  );

const handleTextAndFormatNodes =
  ({
    isBackspaceOrShortcut,
    subtree,
    rangeSelection,
    ancestorNode,
    anchorNode,
    highestEmptyFormatAncestor,
  }: {
    isBackspaceOrShortcut: boolean;
    subtree: ExpressionElement;
    rangeSelection: RangeSelectionRecordType;
    ancestorNode: ExpressionElement;
    anchorNode: ExpressionElement;
    highestEmptyFormatAncestor: ExpressionElement | null;
  }) =>
  (
    contentEditorActiveAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const findNearestTopologicalInDirection = isBackspaceOrShortcut
      ? findPreviousTextOrLineBreak
      : findNextTextOrLineBreak;
    const findNearestTopologicalOppositeDirection = isBackspaceOrShortcut
      ? findNextTextOrLineBreak
      : findPreviousTextOrLineBreak;
    const moveCaretToNearestEndMethod = isBackspaceOrShortcut
      ? moveCaretToDeepEndOfNode
      : moveCaretToDeepStartOfNode;
    const moveCaretToNearestEndMethodMirrored = isBackspaceOrShortcut
      ? moveCaretToDeepStartOfNode
      : moveCaretToDeepEndOfNode;

    const nearestTopologicalTextOrLineBreakNode =
      findNearestTopologicalInDirection(ancestorNode, anchorNode.id);

    const nearestMirrorTopologicalTextOrLineBreakNode =
      !nearestTopologicalTextOrLineBreakNode &&
      findNearestTopologicalOppositeDirection(ancestorNode, anchorNode.id);
    const nearestTargetNode =
      nearestTopologicalTextOrLineBreakNode ||
      nearestMirrorTopologicalTextOrLineBreakNode ||
      null;

    const isPreferredDirection = Boolean(nearestTopologicalTextOrLineBreakNode);

    const isMovingLeft =
      (isBackspaceOrShortcut && isPreferredDirection) ||
      (!isBackspaceOrShortcut && !isPreferredDirection);

    const nearestEndMethod = isPreferredDirection
      ? moveCaretToNearestEndMethod
      : moveCaretToNearestEndMethodMirrored;

    if (highestEmptyFormatAncestor) {
      // delete highest format ancestor. The empty string node within (ancestorNode) is also deleted so we have to also move the caret somewhere else
      // We do this so that a user can break out of a format element e.g.
      // <p><b><em>a|<em></b><p>
      // if the user backspaces here we want to remove <b> as well as <em>
      // (note that at this point the 'a' character has always already been removed)
      return pipe([
        idPassthrough(removeElement(highestEmptyFormatAncestor.id)),
        updateCaretPosition({
          nearestTargetNode,
          nearestEndMethod,
          isMovingLeft,
          rangeSelection,
          ancestorNode,
        }),
      ])(contentEditorActiveAndNextId);
    } else {
      const emptyStringParent = Expressions.getParent(
        EElement(subtree),
        anchorNode.id
      );

      const parentChildren = emptyStringParent
        ? getChildren(emptyStringParent) || []
        : [];

      const isOnlyChild = parentChildren.length === 1;

      // user has deleted characters so the parent of the text node is empty (no text, no linebreak) , but we want the browser to maintain the caret within it so we do NOT delete the text node but instead add a ZWJ to prevent browser deletion.
      if (isOnlyChild) {
        return [
          addZwjToStringElement(anchorNode.id)(contentEditorActiveAndNextId[0]),
          contentEditorActiveAndNextId[1],
        ];
      } else {
        // string element has other siblings e.g. linebreak so we can delete, if we weren't to allow this then we could end up with 2 text adajacent text nodes
        return pipe([
          idPassthrough(removeElement(anchorNode.id)),
          updateCaretPosition({
            nearestTargetNode,
            nearestEndMethod,
            isMovingLeft,
            rangeSelection,
            ancestorNode,
          }),
        ])(contentEditorActiveAndNextId);
      }
    }
  };

// Sometimes the right end of the selection will bleed into the next node. Eg:
// if triple clicking on a heading that is followed by a paragraph you'd expect
// the whole heading to be selected, but the right end of the selection will
// actually be in the paragraph at the 0 offset. If the 0 child is a textNode
// the contentEditor would expect at least for it to be in that text node
// instead of just offset directly from the paragraph. This util bumps the right
// end back into the previous typo node.
export const handleTypoSelectionBleed =
  (subtree: ExpressionElement) =>
  (selection: SelectionRecordType): SelectionRecordType => {
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection || isCollapsed(rangeSelection)) return selection;

    const {rightNodeId, rightOffset} = getLeftRightPosition(rangeSelection);
    const rightEndNode = Expressions.getElementById(
      EElement(subtree),
      rightNodeId
    );
    // Note: When it bleeds into an image figure it tends to go into the 0
    // index of the figure, when it bleeds into an iframe figure it tends
    // to go into the 0 index of the figure div
    const isBleeding =
      rightEndNode &&
      rightOffset === 0 &&
      (isRichTextTopLevelTypographic(rightEndNode) ||
        isFigure(rightEndNode) ||
        isList(rightEndNode) ||
        isNonFormatTypographic(rightEndNode) ||
        getTag(rightEndNode) === 'div');

    if (!isBleeding) return selection;

    const prevTopLevelNode = findPreviousTopological(
      subtree,
      rightEndNode.id,
      isNonFormatTypographic
    );
    if (!prevTopLevelNode) return selection;
    const movedSelection = moveCaretToDeepEndOfNode(prevTopLevelNode.id)(
      subtree
    );
    if (!movedSelection) return selection;
    const {rightNodeId: newRightNodeId, rightOffset: newRightOffset} =
      getLeftRightPosition(movedSelection);
    return setRightEndOfSelection(
      newRightNodeId,
      newRightOffset
    )(rangeSelection);
  };

/*
This is meant to be used after we delete a character in a textNode
eg:
```js
p
 textNode('some text')
 strong
  textNode('a')

// or

p
  strong
    textNode('a')
```
and helps with preserving formatToggles and the textNode and its empty formatNode ancestors.

This is different from the user pressing backspace/delete inside an empty non-format typo node that only has a textNode with ZWJ character
which is handled elsewhere. eg:
```js
p
  textNode(ZWJ)
```

This function 'cages' the caret behavior inside the non-format-typo (eg: top-level-typo, list item, or figcaption)
If the textNode is now empty, it first deletes the empty format tree. Empty anchor/span/bold/italic ancestor format nodes of the
textNode are removed. If there was bold/italic it sets the format node toggles so that if the user starts
typing it'll re-apply that same wrapping, if they click away the empty format node hierarchy would already have been cleaned up.

it'll then try move the caret to the next topological text node inside the non-format-typo node
in the direction of removal (BACKSPACE  CUT = left, DELETE = right), then it'll try looking in the opposite direction.
If there are no text nodes remaining in this non-format-typo node, it'll create a new one and put the caret in there.
 */
const handleEmptyTextNodeAfterContentRemoval =
  (removalKey: typeof BACKSPACE | typeof DELETE | typeof SHORTCUTS.CUT) =>
  (
    contentEditorActiveAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [activeContentEditorState] = contentEditorActiveAndNextId;
    const {subtree, selection} = activeContentEditorState;
    const isEditingRichText = isRichText(subtree);

    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return contentEditorActiveAndNextId;
    const isBackspaceOrShortcut = removalKey === BACKSPACE;
    const anchorNode = Expressions.getElementById(
      subtree.data,
      rangeSelection.anchorNodeId
    );

    if (anchorNode === null) {
      return contentEditorActiveAndNextId;
    }

    if (!isStringElement(anchorNode)) {
      return contentEditorActiveAndNextId;
    }

    const isStringAnchorNodeEmpty = !Expressions.getText(anchorNode.data);

    if (!isStringAnchorNodeEmpty) {
      return contentEditorActiveAndNextId;
    }

    const highestEmptyFormatAncestor = findHighestEmptyFormatAncestor(
      subtree,
      anchorNode
    );

    const ancestorNode = isEditingRichText
      ? (Expressions.closestElement(
          EElement(subtree),
          rangeSelection.anchorNodeId,
          isRichTextNonFormatTypographic
        ) ??
        (() => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Could not find closestNonFormatTypographic');
          }
          return null;
        })())
      : subtree;

    if (ancestorNode === null) {
      return contentEditorActiveAndNextId;
    }

    return pipe([
      idPassthrough(setFormatToggles(highestEmptyFormatAncestor)),
      insertLineBreakForNestedList(ancestorNode),
      handleTextAndFormatNodes({
        isBackspaceOrShortcut,
        subtree,
        rangeSelection,
        ancestorNode,
        anchorNode,
        highestEmptyFormatAncestor,
      }),
    ])(contentEditorActiveAndNextId);
  };

export const handleBackspaceDeleteKeyInTextNode =
  (removalKey: typeof BACKSPACE | typeof DELETE) =>
  (
    contentEditorActiveAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const removeCharInDirectionMethod =
      removalKey === BACKSPACE ? removeCharToLeft : removeCharToRight;

    return pipe([
      idPassthrough(removeCharInDirectionMethod),
      handleEmptyTextNodeAfterContentRemoval(removalKey),
    ])(contentEditorActiveAndNextId);
  };

// 1) removes element
// 2) attempts to move caret to left or right from deleted element
// 3) if not, attempts to move caret in opposite direction
// 4) if not, top level has no children so creates an empty paragraph and moves into that
export const removeTopLevelElementAndMoveCaret =
  ({
    preferredDirection,
    elementIdToRemove,
  }: {
    // should the caret move left or right after deletion
    preferredDirection: 'left' | 'right';
    elementIdToRemove: string;
  }) =>
  (
    stateAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state] = stateAndNextId;
    const {subtree} = state;
    const closestTopLevelElement = Expressions.getElementById(
      EElement(subtree),
      elementIdToRemove
    );

    if (!closestTopLevelElement) {
      return stateAndNextId;
    }

    const isRemovedNodeAComponent = isUserComponent(
      closestTopLevelElement.type
    );

    const {targetElement, moveCaretDirection} = findMoveTarget({
      subtree,
      closestTopLevelElement,
      preferredDirection,
    });

    return pipe([
      idPassthrough(removeElement(elementIdToRemove)),
      moveToTargetOrAddParagraph({targetElement, moveCaretDirection}),
      isRemovedNodeAComponent
        ? idPassthrough(setShouldSyncToComponentMap(true))
        : identity,
      isRemovedNodeAComponent
        ? idPassthrough(
            setRemovedComponentIds([getName(closestTopLevelElement.type)])
          )
        : identity,
    ])(stateAndNextId);
  };

export const handleArrowKeyWhenNodeSelected =
  (key: typeof UP | typeof LEFT | typeof DOWN | typeof RIGHT | number) =>
  (
    stateAndNextId: ContentEditorActiveAndNextId
  ): ContentEditorActiveAndNextId => {
    const [state, nextId] = stateAndNextId;
    const {selection, subtree} = state;
    const nodeSelection = assertNodeSelectionRecordType(selection);

    if (!nodeSelection?.nodeId) return stateAndNextId;

    if (key === UP || key === LEFT) {
      return moveToDeepLeftSiblingOrCreateParagraph(stateAndNextId);
    } else if (key === DOWN || key === RIGHT) {
      const element = Expressions.getElementById(
        EElement(subtree),
        nodeSelection.nodeId
      );

      if (element === null) return stateAndNextId;

      const figureType = getFigureTypeById(subtree, nodeSelection.nodeId);
      if (figureType === 'image') {
        const figcaption = findElementDescendant(element, isFigCaption);

        if (figcaption === undefined) return stateAndNextId;

        const updatedSelection = moveCaretToDeepStartOfNode(figcaption.id)(
          subtree
        );
        if (updatedSelection === null) return stateAndNextId;

        return [setSelection(updatedSelection)(state), nextId];
      } else if (
        figureType === 'video' ||
        isEmbed(element) ||
        isCodeBlock(element) ||
        isUserComponent(element.type)
      ) {
        const childrenExpr = Expressions.getIn(subtree.data, [
          {in: 'Record', at: 'children'},
        ]);

        if (!childrenExpr) {
          return stateAndNextId;
        }

        const children = Expressions.getList(childrenExpr);
        if (!children) {
          return stateAndNextId;
        }

        return [moveToDeepSibling('right')(state), nextId];
      }
    }
    return stateAndNextId;
  };

export const isHiddenFromContentEditable = (
  element: ExpressionElement
): boolean => isCodeBlock(element) || isUserComponent(element.type);

// Takes in the ContentEditor state, applies markdown formatting to the
// selected text node, and then restores the caret position.
export const handleMarkdownFormatting =
  (
    key: number,
    seedId: string,
    plugins: Plugins.PluginsType,
    components: Component.ComponentMap
  ) =>
  (state: ContentEditorActive) => {
    const {subtree, selection} = state;

    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (!rangeSelection) return state;

    const allowLinksInContent = canHaveLinksInContent(state);
    const {anchorNodeId} = rangeSelection;

    const anchor = Expressions.getElementById(EElement(subtree), anchorNodeId);
    if (!anchor) return state;
    const parentNode = Expressions.getParent(EElement(subtree), anchorNodeId);
    if (!parentNode) return state;

    const parentTag = getTag(parentNode);

    const textValue = Expressions.getText(anchor.data) || '';

    let selectionIndex = rangeSelection.anchorOffset;

    // If the user is adding the first parenthesis of a link, we don't want
    // to insert the selection marker in the middle of the link. This will
    // stop the HTML conversion. Instead, we find the end of the link and
    // add the selection marker there.
    if (key === OPENING_PARENTHESES) {
      const linkPattern = /(\[.*\])(\((http)(?:s)?(\:\/\/).*\))/g;
      const linkText = textValue.match(linkPattern);
      const linkIndexInTextValue = textValue.search(linkPattern);
      if (linkText) {
        selectionIndex = linkIndexInTextValue + linkText[0].length;
      }
    }

    // Places a marker in the text value where the where we want the selection to be.
    // This will make it easier to reset the selection after the node is updated.
    const textValueWithSelectionMarker = insert(
      textValue,
      selectionIndex,
      // Need to add a space between characters because marked does not detect an
      // underscore if a non-whitespace character is adjacent to it
      ` ${PASTE_SELECTION_MARKER}`
    );

    // Convert the markdown text to HTML, then wrap it in the appropriate parent tag
    // This ensures you can add markdown syntax to non-paragraph block elements (like blockquotes)
    const textValueAsHtml = marked.parseInline(textValueWithSelectionMarker);
    const textValueAsWrappedHtml = `<${parentTag}>${textValueAsHtml}</${parentTag}>`;

    const {jsonContentNodes, nextId} = rteHtmlToJsonNodes(
      textValueAsWrappedHtml,
      {
        supportHtmlEmbeds: true,
        seedId,
      }
    );
    const newEl = Tree.create(components).getElementWithJSONNodes(
      jsonContentNodes,
      plugins
    );

    return pipe<ContentEditorActive>([
      updateSubtree(insertElement(newEl, anchorNodeId, 'after')),
      removeElement(anchorNodeId),
      (_state) => {
        const result = enforceTagStructure({allowLinksInContent})(
          pair(_state.subtree, nextId)
        );
        return _state.set('subtree', getFirst(result));
      },
      restoreSelectionAfterPaste,
      (_state) => {
        const isAtEnd = caretIsAtEndOfTextNode(_state.selection)(_state);
        // Since we add the extra space in the textValueWithSelectionMarker, remove it
        // But only if in the middle of a paragraph.
        if (!isAtEnd) {
          return removeCharToLeft(_state);
        }

        // If at the end of a paragraph, removing the extra space will cause selection issues
        return _state;
      },
    ])(state);
  };

export const handleContentEditorAddElement =
  (
    addType: CONTENT_EDITOR_ADD_TYPES,
    components: Component.ComponentMap,
    plugins: Plugins.PluginsType,
    iframeFigureData?: IframeFigureData,
    focusedSymbolNativeIdPath?: NativeId[],
    componentType?: ComponentMetadata['componentType'],
    targetNodeId?: string
  ) =>
  (stateAndNextId: ContentEditorActiveAndNextId): ContentEditorActive => {
    const [initialState, nextCreatedElementId] = stateAndNextId;

    // If targetNodeId is provided, update the selection to that node before adding
    let state = initialState;
    if (targetNodeId && addType === ADD_COMPONENT) {
      const richTextElementId = state.subtree?.id;

      if (targetNodeId !== richTextElementId) {
        const targetSelection = NodeSelectionRecord({
          nodeId: targetNodeId,
          nodeClientRect: null,
        });
        state = state.set('selection', targetSelection);
      }
    }
    const creationResult = contentEditorCreation[addType](
      nextCreatedElementId,
      {plugins, components, componentType}
    );
    const nextId = getFirst(creationResult).id;
    const expressionElement = getSecond(creationResult);

    const stateWithElementAdded = pipe([
      addSlashCommandElementAfterTopLevel(expressionElement),
      selectNewElement[addType](nextCreatedElementId),
      hideToolbar,
    ])(state);

    if (addType === ADD_IMAGE) {
      return pipe([
        idPassthrough(setActiveToolbar(TOOLBARS.FIGURE)),
        togglePlaceholderFigcaption,
        idPassthrough((_state) =>
          _state.setIn(
            ['activeUploads', expressionElement.id],
            ActiveUploadRecord()
          )
        ),
      ])([stateWithElementAdded, nextId])[0];
    }

    if (addType === ADD_MEDIA && iframeFigureData) {
      return pipe([
        setIframeFigure({
          nodeId: expressionElement.id,
          iframeFigureData,
        }),
        setActiveToolbar(TOOLBARS.FIGURE),
      ])(stateWithElementAdded);
    }

    if (addType === ADD_AI_PARAGRAPH && focusedSymbolNativeIdPath) {
      return getFirst(
        pipe([
          idPassthrough(
            setAiData({
              outputFormat: 'PlainText',
              userPrompt: '',
            })
          ),
          triggerAiGeneration(
            'PlainText',
            plugins,
            components,
            focusedSymbolNativeIdPath,
            'slash_command'
          ),
        ])(pair(stateWithElementAdded, nextId))
      );
    }

    if (addType === ADD_COMPONENT) {
      if (
        isEditingDynamicCMSContent(state) ||
        isEditingRichTextComponentProp(state)
      ) {
        return setActiveToolbar(TOOLBARS.COMPONENTSETTINGS)(
          stateWithElementAdded
        );
      }
      return pipe([idPassthrough(setShouldCloseAfterComponentAdd(true))])([
        stateWithElementAdded,
        nextId,
      ])[0];
    }

    return stateWithElementAdded;
  };

// Changes the alt text of the currently selected figure node's image from the settings modal
export const updateFigureAltText =
  (value: string) => (state: ContentEditorActive) => {
    const {subtree, selection} = state;
    if (!selection) return state;

    const selectedNodeId = getFigureIdFromSelection(subtree, selection);
    if (!selectedNodeId) return state;

    const imageElement = findDeepFigureChildElement(subtree, selectedNodeId);
    if (!imageElement) return state;

    return pipe([
      updateSubtree(setImageAltText(imageElement, value)),
      shouldUseReactRender,
    ])(state);
  };

export const handleEnterInFigcaption = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {subtree, selection} = getFirst(stateAndId);

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return stateAndId;

  const subtreeExpr = EElement(subtree);

  const parent = Expressions.getParent(
    subtreeExpr,
    rangeSelection.anchorNodeId
  );

  if (!parent) return stateAndId;

  const figureElement = Expressions.closestElement(
    subtreeExpr,
    parent.id,
    isFigure
  );

  if (!figureElement) return stateAndId;

  return pipe([
    addParagraphWithEmptyString(figureElement.id, 'after'),
    idPassthrough(shouldUseReactRender),
  ])(stateAndId);
};

export const handleEnterOnFigure = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {selection, subtree} = getFirst(stateAndId);
  const nodeSelection = assertNodeSelectionRecordType(selection);

  const subtreeExpr = EElement(subtree);

  if (!nodeSelection?.nodeId) return stateAndId;

  const el = Expressions.getElementById(subtreeExpr, nodeSelection.nodeId);

  if (!el) return stateAndId;

  return pipe([
    addParagraphWithEmptyString(el.id, 'after'),
    idPassthrough(shouldUseReactRender),
  ])(stateAndId);
};

/**
 * Inserts a node at the caret position between non-text elements in the content tree.
 * This is specifically used when the caret is positioned between elements (like <br> tags),
 * rather than within text content. The function uses the selection's anchorNodeId and offset
 * to determine the exact insertion point.
 */
export const insertNodeNextToSiblings =
  (nodeToInsert: ExpressionElement) =>
  (state: ContentEditorActive): ContentEditorActive => {
    const {subtree, selection} = state;
    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (!rangeSelection) return state;

    const subtreeExpr = EElement(subtree);
    const path = getKeyPathToElement(subtreeExpr, rangeSelection.anchorNodeId);

    if (!subtreeExpr || !path) return state;

    const childrenPath: ExpressionPath = [
      {in: 'Element'},
      {in: 'Record', at: 'children'},
      {in: 'List', index: 0},
    ];

    const updatedSubtree = updateElementListAtPath(
      subtreeExpr,
      [...path, ...childrenPath],
      (list: Expression[], index: number): Expression => {
        const elementsBefore = list.slice(index, rangeSelection.anchorOffset);
        const elementsAfter = list.slice(rangeSelection.anchorOffset);
        return EList([
          ...elementsBefore,
          EElement(nodeToInsert),
          ...elementsAfter,
        ]);
      }
    );

    return updatedSubtree
      ? state.set('subtree', Expressions.getElement(updatedSubtree) || subtree)
      : state;
  };

/**
 * Handles Enter key press in a typographic node by either:
 * - Adding a new element after the current one if caret is at end
 * - Adding a new element before if caret is at start
 * - Splitting the current element at caret position otherwise
 */
export const handleEnterInTypoNode = ([
  state,
  nextId,
]: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
  const {subtree, selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return [state, nextId];
  const {anchorNodeId} = rangeSelection;

  // Get the closest top level element (typo node equivalent in Expression system)
  const typoElement = getClosestTopLevelElement(anchorNodeId, subtree);

  if (!typoElement) return [state, nextId];

  const [_, newElement] = isRichText(subtree)
    ? createParagraphWithZwj(nextId)
    : createEmptyString({id: nextId})(state);

  if (caretIsAtTheEndOfTheNode(typoElement.id)(state)) {
    // Add paragraph after current element
    return pipe<ContentEditorActiveAndNextId>([
      idPassthrough(
        setSubtree(
          Expressions.getElement(
            addElementAtAnchor(
              EElement(subtree),
              newElement,
              typoElement,
              'after'
            )
          ) || subtree
        )
      ),
      idPassthrough((_state) =>
        setSelection(
          moveCaretToDeepStartOfNode(nextId)(_state.subtree) ?? rangeSelection
        )(_state)
      ),
    ])([state, nextId]);
  } else if (caretIsAtTheStartOfTheNode(typoElement.id)(state)) {
    return pipe<ContentEditorActiveAndNextId>([
      idPassthrough(
        setSubtree(
          Expressions.getElement(
            addElementAtAnchor(
              EElement(subtree),
              newElement,
              typoElement,
              'before'
            )
          ) || subtree
        )
      ),
      idPassthrough((_state) =>
        setSelection(
          moveCaretToDeepStartOfNode(rangeSelection.anchorNodeId)(subtree) ??
            rangeSelection
        )(_state)
      ),
    ])([state, nextId]);
  } else {
    // Split the current element at caret position when in middle of text
    return pipe<ContentEditorActiveAndNextId>([
      ensureCaretIsInATextNode,
      splitNodeOnCollapsedRangeInTextNode(typoElement.id),
      idPassthrough(shouldUseReactRender),
    ])([state, nextId]);
  }
};

const addNewListItem =
  (anchorId: string, position: 'before' | 'after' | 'prepend' | 'append') =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const itemId = getSecond(stateAndId);
    const stringId = incrementGuid(itemId);

    const newNode = {
      id: itemId,
      type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
      data: ERecord({
        list: ERecord({
          type: EText('item'),
        }),
        children: EList([
          EElement({
            id: stringId,
            type: qualify(Plugins.name('Basic'), Component.name('String')),
            data: EText(ZWJ_RAW),
          }),
        ]),
      }),
    };

    return pair(
      pipe<ContentEditorActive>([
        updateSubtree(
          updateElementData((data) =>
            addElementAtAnchorById(data, newNode, anchorId, position)
          )
        ),
        (_state) =>
          setSelection(
            moveCaretToDeepStartOfNode(itemId)(_state.subtree) ||
              _state.selection
          )(_state),
      ])(state),
      incrementGuid(stringId)
    );
  };

export const handleEnterInEmptyListItem =
  (listNode: ExpressionElement, listItem: ExpressionElement) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const {subtree} = getFirst(stateAndId);
    const listChildren = getChildren(listNode) || [];
    const lastItem = listChildren[listChildren.length - 1];
    const parentListItem = getClosestListItemElement(
      EElement(subtree),
      listNode.id
    );

    if (!lastItem) return stateAndId;

    if (lastItem.id === listItem.id)
      return pipe([
        // is  nested list
        parentListItem
          ? addNewListItem(parentListItem.id, 'after')
          : addParagraphWithEmptyString(listNode.id, 'after'),
        idPassthrough(
          listChildren.length === 1
            ? removeElement(listNode.id)
            : removeElement(listItem.id)
        ),
        idPassthrough(shouldUseReactRender),
      ])(stateAndId);

    // is nested list
    if (parentListItem) {
      const parentList = Expressions.getParent(
        EElement(subtree),
        parentListItem.id
      );
      if (parentList && isList(parentList)) {
        const newListItemId = getSecond(stateAndId);
        return pipe([
          addNewListItem(parentListItem.id, 'after'),
          ([state, newListId]: ContentEditorActiveAndNextId) =>
            pair(
              updateSubtree(
                handleList(newListItemId, listItem.id, listNode.id, newListId)
              )(state),
              incrementGuid(newListId)
            ),
          idPassthrough(shouldUseReactRender),
        ])(stateAndId);
      }
    }

    // Splitting list into 2 lists
    return pipe<ContentEditorActiveAndNextId>([
      (_stateAndId) => {
        const _state = getFirst(_stateAndId);
        const subtreeAndId = splitList(
          listNode,
          listItem.id
        )(pair(_state.subtree, getSecond(_stateAndId)));
        return pair(
          setSubtree(getFirst(subtreeAndId))(_state),
          getSecond(subtreeAndId)
        );
      },
      addParagraphWithEmptyString(listNode.id, 'after'),
      idPassthrough(shouldUseReactRender),
    ])(stateAndId);
  };

export const handleEnterInListNode = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const state = getFirst(stateAndId);
  const {subtree, selection} = state;
  const rangeSelection = assertRangeSelectionRecordType(selection);
  if (!rangeSelection) return stateAndId;
  const listItem = getClosestListItemElement(
    EElement(subtree),
    rangeSelection.anchorNodeId
  );

  if (!listItem) return stateAndId;

  if (isEmptyTypoOrLineBreak(listItem)) {
    const listNode = getClosestListElement(
      EElement(subtree),
      rangeSelection.anchorNodeId
    );
    if (!listNode) return stateAndId;
    return handleEnterInEmptyListItem(listNode, listItem)(stateAndId);
  }

  if (caretIsAtTheStartOfTheNode(listItem.id)(state))
    return pipe([
      addNewListItem(listItem.id, 'before'),
      // keep the selection in the original list item (which is now after the new empty list item)
      idPassthrough(setSelection(rangeSelection)),
    ])(stateAndId);

  if (caretIsAtTheEndOfTheNode(listItem.id)(state))
    return pipe([
      addNewListItem(listItem.id, 'after'),
      idPassthrough(shouldUseReactRender),
    ])(stateAndId);

  return pipe([
    ensureCaretIsInATextNode,
    splitNodeOnCollapsedRangeInTextNode(listItem.id),
    idPassthrough(shouldUseReactRender),
  ])(stateAndId);
};

export const handleEnterKey =
  (seedId: string) => (state: ContentEditorActive) => {
    const {selection, subtree} = state;
    const stateAndId = pair(state, seedId);

    if (selection.type === 'Node')
      return getFirst(handleEnterOnFigure(stateAndId));

    if (selection.type === 'Range') {
      const typoNode = getClosestTopLevelTypoElement(
        selection.anchorNodeId,
        subtree
      );

      if (typoNode) return getFirst(handleEnterInTypoNode(stateAndId));

      const listNode = getClosestListElement(
        EElement(subtree),
        selection.anchorNodeId
      );

      if (listNode) return getFirst(handleEnterInListNode(stateAndId));

      const parent = Expressions.getParent(
        EElement(subtree),
        selection.anchorNodeId
      );

      if (parent && isFigCaption(parent))
        return getFirst(handleEnterInFigcaption(stateAndId));
    }

    return state;
  };

export const savePendingLinkChanges = (
  stateAndId: ContentEditorActiveAndNextId
): ContentEditorActiveAndNextId => {
  const {pendingLinkToolbarChanges} = getFirst(stateAndId);
  if (!pendingLinkToolbarChanges) return stateAndId;
  const {linkNodeId} = pendingLinkToolbarChanges;
  if (!linkNodeId) return stateAndId;

  return pipe([
    idPassthrough(
      updateSubtree(
        updateElementById(linkNodeId, updateLinkNode(pendingLinkToolbarChanges))
      )
    ),
    removeLinkIfEmpty(linkNodeId),
    idPassthrough(clearPendingLinkToolbarChanges),
  ])(stateAndId);
};

const dissocId = dissoc('id');

export const updateImageData = (
  {
    url,
    assetId,
  }: {
    url?: string;
    assetId?: string;
  },
  imageFigureId: string
) =>
  updateElementById(imageFigureId, (imageFigure) => {
    const image = findElementDescendant(imageFigure, isImageElement);
    if (!image) return imageFigure;
    return updateElementById(
      image.id,
      updateElementData((data) => {
        const urlString = url || '';
        const props = Expressions.getRecord(data);
        const attr =
          props && props.attr ? Expressions.getRecord(props.attr) : {};
        const src = attr && attr.src ? Expressions.getText(attr.src) : null;
        const imgJson =
          props && props.img
            ? Expressions.getLiteral(props.img)?.value
            : undefined;
        const img = imgJson ? getObject(imgJson) : undefined;
        const imgId = img ? img.id : undefined;

        if (urlString === EXAMPLE_IMG_URL)
          return src === urlString && !imgId
            ? data
            : imgId
              ? ERecord({
                  ...props,
                  img: ELiteral({
                    name: qualify(Plugins.name('Asset'), typeName('Image')),
                    value: img ? dissocId(img) : {},
                  }),
                  attr: ERecord({...attr, src: EText(urlString)}),
                })
              : ERecord({
                  ...props,
                  attr: ERecord({...attr, src: EText(urlString)}),
                });

        return src === urlString && (!assetId || imgId === assetId)
          ? data
          : assetId
            ? ERecord({
                ...props,
                img: ELiteral({
                  name: qualify(Plugins.name('Asset'), typeName('Image')),
                  value: {...img, id: assetId},
                }),
                attr: ERecord({...attr, src: EText(urlString)}),
              })
            : ERecord({
                ...props,
                attr: ERecord({...attr, src: EText(urlString)}),
              });
      })
    )(imageFigure);
  });

export const cleanUpActiveUploads =
  (activeUploads: Map<string, ActiveUploadRecordType>) =>
  (subtree: ExpressionElement): ExpressionElement => {
    activeUploads.forEach((upload, imageFigureId) => {
      if (upload.isReplacement) return;
      subtree = updateImageData({url: EXAMPLE_IMG_URL}, imageFigureId)(subtree);
    });
    return subtree;
  };

export const removeActiveUpload =
  (uploadId: string) => (state: ContentEditorActive) =>
    state.deleteIn(['activeUploads', uploadId]);

export const getActiveUpload = (
  activeUploads: Map<string, ActiveUploadRecordType>,
  imageFigureId: string
): ActiveUploadRecordType =>
  activeUploads.find((_upload, uploadId) => uploadId === imageFigureId);

export const updateImageProgressUpload = (
  imageFigureId: string,
  progress: number,
  state: ContentEditorActive
) => state.setIn(['activeUploads', imageFigureId, 'progress'], progress);

export const updateImageError = (
  imageFigureId: string,
  state: ContentEditorActive
) => state.setIn(['activeUploads', imageFigureId, 'error'], true);

export const removeImage = (imageFigureId: string) =>
  pipe([
    addParagraphWithEmptyString(imageFigureId, 'after'),
    idPassthrough(removeElement(imageFigureId)),
  ]);

export const updateImageSpinnerPositions = (
  imgNodeRects: {[key: string]: Box},
  state: ContentEditorActive
) =>
  state.update('activeUploads', (activeUploads) =>
    activeUploads.map((upload, imageFigureId) => {
      // Images that are no longer in the subtree (because of undo or delete)
      // should have their client rect reset so the spinner is removed
      const imgNodeRect = imgNodeRects[imageFigureId] || null;

      return upload.set('nodeClientRect', imgNodeRect);
    })
  );

export const cancelVideo = pipe([
  setActiveToolbar(TOOLBARS.__LEGACY__ADD),
  shouldUseReactRender,
]);

export const convertTextBlockToDiv = (
  subtree: ExpressionElement
): ExpressionElement => {
  if (
    !isRichText(subtree) &&
    isDivBlock(subtree) &&
    getTagFromData(subtree) === 'div' &&
    !isDynamoChildrenBinding(subtree.data) &&
    isEmptyTypoOrLineBreak(subtree)
  )
    // If we were editing a text block in the designer-canvas and we deleted all the content
    // with the ContentEditor, then convert it back to a regular div element.
    return updateElementData((data) =>
      ERecord({
        ...Expressions.getRecord(data),
        text: EBoolean(false),
        children: EList([]),
      })
    )(subtree);

  return subtree;
};

const nonEmptyEl = (el: ExpressionElement) =>
  isLineBreakElement(el) ||
  isFigure(el) ||
  isImageElement(el) ||
  isEmbed(el) ||
  isCodeBlock(el) ||
  isUserComponent(el.type);

const isEmptyWithChildren = (el: ExpressionElement) =>
  isEmptyValue(getAllTextInElement(el)) &&
  !isStringElement(el) &&
  !findElementDescendant(el, nonEmptyEl) &&
  (getChildren(el) || emptyArray).length > 0;

const removeDefaultEmptyContent = (
  subtree: ExpressionElement
): ExpressionElement => {
  if (isEmptyWithChildren(subtree))
    return updateElementData((data) =>
      ERecord({
        ...Expressions.getRecord(data),
        children: EList([]),
      })
    )(subtree);

  return subtree;
};

// This is used for cleaning the subtree when passing it on to another store.
// eg: saving changes and integrating the subtree into the page components.
export const cleanSubtreeForSave = (
  state: ContentEditorActive,
  seedId: string
): Pair<ExpressionElement | null, string> => {
  const [savedState, nextId] = savePendingLinkChanges(pair(state, seedId));

  const {subtree, activeUploads} = savedState;

  try {
    return pair(
      pipe([
        cleanUpActiveUploads(activeUploads),
        hidePlaceholderFigcaption,
        state.isOnPrimaryLocale ? convertTextBlockToDiv : identity,
        state.isOnPrimaryLocale ? removeDefaultEmptyContent : identity,
        handleLinkDefaults,
        removeConsecutiveSpaces,
        cleanUpGhostLinebreaksAndEmptyText,
      ])(subtree),
      nextId
    );
  } catch (e: any) {
    if (process.env.NODE_ENV === 'development') {
      console.error('There was an error in the ContentEditorStore reducer', e);
    }

    // Note: For now we'll just return the uncleaned subtree if an error
    // happens cleaning it. This limits data loss (compared to reverting to
    // the pre-change state), since we're reporting the error via datadog, we
    // can address any issues with json-patch migrations if it leads to data
    // corruption, and/or tag enforcement.
    return pair(subtree, nextId);
  }
};

// Checks if the node we're editing is nested inside an <a> node, (used in ContentEditorStore)
// Note: This should not check the node itself, use canHaveLinksInContent() for that
export const isElementNestedInAnchor = (
  nativeIdPath: Array<NativeId>,
  components: Component.ComponentMap
) => {
  const elementNativeId = nativeIdPath[nativeIdPath.length - 1];
  if (!elementNativeId) return false;
  const componentType = getCurrentComponentType(
    components,
    nativeIdPath.slice(0, -1)
  );
  const component = components.get(componentType);
  if (!component) return false;
  const elementId = getGuid(elementNativeId);
  const root = Component.getRender(component);
  // Note: Because the text node we're in could be inside a component and that
  // component could be inside an anchor we should be looking at all components
  // in the `nativeIdPath`. This code only searches within the page or component
  // that the element identified by `nativeIdPath` lives in.
  // There's a bigger issue here where a component used on multiple pages isRichText
  // nested in an anchor on one of the pages and edited on the other couldresult
  // result in nested links so it reuqires a better solution.
  // Additionally, because content can pe passed around through component props
  // we cannot easily tell if we might end up with nested links.
  // see https://github.com/webflow/webflow/issues/14433)
  const parentNode = Expressions.getParent(root, elementId);
  if (!parentNode) return false;
  return Boolean(Expressions.closestElement(root, parentNode.id, isLink));
};

const GROUPABLE_ACTION_STEPS = [
  CONTENT_EDITOR_CHARS_INSERTED,
  CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED,
  CONTENT_EDITOR_TEXT_VALUE_REPLACED,
];

const cleanUpCancelledImages = (
  stackItem: ContentEditorUndoStackItemRecordType
): ((subtree: ExpressionElement) => ExpressionElement) => {
  const {action} = stackItem;

  const imageFigureId =
    action.type === CONTENT_EDITOR_ELEMENT_ADDED &&
    action.payload.addType === ADD_IMAGE &&
    action.payload.id;

  if (imageFigureId)
    return updateImageData({url: EXAMPLE_IMG_URL}, imageFigureId);

  return identity;
};

// We record undo steps as they happen, and then when actually undoing we group
// some actions together that happen close to each other in time and throw out
// the undo steps we don't need at that time
const findRecentStackItemIndex = (
  undoStack: List<ContentEditorUndoStackItemRecordType>
) => {
  // Recent item is the last one in stack unless we can group quick actions together
  let index = undoStack.size - 1;
  const lastStackItem = undoStack.get(index);

  // for flow
  if (!lastStackItem) {
    return index;
  }

  const lastStackItemTime = lastStackItem.action.timestamp;

  for (let i = undoStack.size - 1; i >= 0; i--) {
    const stackItem = undoStack.get(i);

    if (stackItem) {
      const actionType = stackItem.action.type;
      const actionPayload = stackItem.action.payload;
      const recentStep = lastStackItemTime - stackItem.action.timestamp <= 1500;
      const groupableStep = GROUPABLE_ACTION_STEPS.includes(actionType);
      const spaceKeyDown =
        actionType === CONTENT_EDITOR_KEY_DOWN && actionPayload.key === SPACE;

      if (recentStep && (groupableStep || spaceKeyDown)) {
        index = i;
      } else {
        break;
      }
    }
  }
  return index;
};

/**
 * Extracts top-level component element IDs from a subtree.
 * Only checks direct children of the subtree, not nested components.
 */
const getComponentIds = (
  subtree: ExpressionElement
): Record<string, ExpressionElement> => {
  const children = getChildren(subtree) || [];
  const topLevelComponents = children.filter((element) =>
    isUserComponent(element.type)
  );

  const componentsByElementId = topLevelComponents.reduce<
    Record<string, ExpressionElement>
  >((acc, el) => {
    acc[el.id] = el;
    return acc;
  }, {});

  return componentsByElementId;
};

/**
 * Checks if the component set changed between two subtrees.
 * Used to determine if a component was added or removed during an undo or redo operation.
 */
const getComponentSetDiff = (
  oldSubtree: ExpressionElement,
  newSubtree: ExpressionElement
): {componentsChanged: boolean; removedIds: string[]; addedIds: string[]} => {
  const oldComponentIds = getComponentIds(oldSubtree);
  const newComponentIds = getComponentIds(newSubtree);

  const removedIds = [];
  const addedIds = [];

  for (const id in oldComponentIds) {
    if (!newComponentIds?.[id]) {
      removedIds.push(getName(oldComponentIds[id]!.type));
    }
  }

  for (const id in newComponentIds) {
    if (!oldComponentIds?.[id]) {
      addedIds.push(getName(newComponentIds[id]!.type));
    }
  }

  return {
    componentsChanged: removedIds.length > 0 || addedIds.length > 0,
    removedIds,
    addedIds,
  };
};

/**
 * Diffs the top-level component set between the pre-paste and post-paste
 * subtrees and flags the store so the sync engine fires the component
 * add/remove analytics actions.
 */
export const handlePasteComponentDiff =
  (oldSubtree: ExpressionElement, newSubtree: ExpressionElement) =>
  (state: ContentEditorActive): ContentEditorActive => {
    const {componentsChanged, removedIds, addedIds} = getComponentSetDiff(
      oldSubtree,
      newSubtree
    );

    return pipe([
      componentsChanged ? setShouldSyncToComponentMap(true) : identity,
      removedIds.length > 0 ? setRemovedComponentIds(removedIds) : identity,
      addedIds.length > 0 ? setAddedComponentIds(addedIds) : identity,
      addedIds.length > 0 ? setComponentChangeSource('paste') : identity,
    ])(state);
  };

const setUndoStack =
  (undoStack: List<ContentEditorUndoStackItemRecordType>) =>
  (state: ContentEditorActive) =>
    state.set('undoStack', undoStack);

const setRedoStack =
  (redoStack: List<ContentEditorUndoStackItemRecordType>) =>
  (state: ContentEditorActive) =>
    state.set('redoStack', redoStack);

/**
 * The undo stack initializes with an item that has the subtree and selection as
 * it was when starting to edit with the ContentEditor. The last item in the
 * undoStack will always refer to the 'current' subtree/selection minus any
 * half-states (ie: reducers that may have been called that we don't create an
 * undo step for). There should always be at least one item in the undoStack.
 *
 * Certain actions can be grouped together if they happen fast enough. We only
 * group them when clicking undo.
 */
export const handleUndo = (state: ContentEditorActive) => {
  const {undoStack, redoStack, activeUploads} = state;

  if (undoStack.size === 1) return state;

  // Note: recentUndoStackItemIndex will be the same as currentUndoStackItem
  // unless a bunch of undo steps are grouped together. We're going to end up
  // only preserving the last thing in the group by moving it to the redoStack.

  // TODO (Yoav): maybe in a future PR we can drop grouped items in addStackItem
  // otherwise we're keeping them in memory and will never use them.
  const currentUndoStackItem = undoStack.last();
  const recentUndoStackItemIndex = findRecentStackItemIndex(undoStack);
  const recentUndoStackItem = undoStack.get(recentUndoStackItemIndex);
  const newCurrentUndoStackItem = undoStack.get(recentUndoStackItemIndex - 1);

  // for flow
  if (
    !currentUndoStackItem ||
    !recentUndoStackItem ||
    !newCurrentUndoStackItem
  ) {
    return state;
  }

  const undoImageUpload =
    recentUndoStackItem.action.type === CONTENT_EDITOR_ELEMENT_ADDED &&
    recentUndoStackItem.action.addType === ADD_IMAGE;
  const undoInProgressImageUpload =
    undoImageUpload && activeUploads.get(recentUndoStackItem.action.payload.id);

  // Replace the blob urls so if we redo this we can't end up saving it.
  const redoStackItem = undoInProgressImageUpload
    ? currentUndoStackItem.set(
        'subtree',
        cleanUpCancelledImages(currentUndoStackItem)(state.subtree)
      )
    : currentUndoStackItem;

  // If we're at the most recent undo item, then preserve whatever the selection
  // is. Normally updating the selection isn't worth an undo step, but when/if
  // the user decides to redo back to here we want to restore whatever their
  // last selection was before starting undo.
  const redoStackItemWithSelection = !redoStack.size
    ? redoStackItem.set('selection', state.selection)
    : redoStackItem;

  // Check if any components were added or removed during this undo operation so
  // we can sync the component map if needed.
  const {componentsChanged, removedIds, addedIds} = getComponentSetDiff(
    state.subtree,
    newCurrentUndoStackItem.subtree
  );

  return pipe([
    // If we're skipping over some grouped undo steps. We want to move the
    // currentUndoStackItem to the redoStack, and drop the grouped items from the
    // undoStack.
    setUndoStack(undoStack.slice(0, recentUndoStackItemIndex)),
    setRedoStack(redoStack.unshift(redoStackItemWithSelection)),
    setSubtree(newCurrentUndoStackItem.subtree),
    setSelection(newCurrentUndoStackItem.selection),
    undoInProgressImageUpload
      ? removeActiveUpload(
          recentUndoStackItem.action.payload.imageFigureNode.id
        )
      : identity,
    shouldUseReactRender,
    setActiveToolbarFromState,
    componentsChanged ? setShouldSyncToComponentMap(true) : identity,
    removedIds.length > 0 ? setRemovedComponentIds(removedIds) : identity,
    addedIds.length > 0 ? setAddedComponentIds(addedIds) : identity,
    addedIds.length > 0 ? setComponentChangeSource('undo/redo') : identity,
  ])(state);
};

export const handleRedo = (state: ContentEditorActive) => {
  const {undoStack, redoStack} = state;
  const presentStackItem = redoStack.first();

  if (!presentStackItem) {
    return state;
  }

  const {componentsChanged, removedIds, addedIds} = getComponentSetDiff(
    state.subtree,
    presentStackItem.subtree
  );

  return pipe([
    setUndoStack(undoStack.push(presentStackItem)),
    setRedoStack(redoStack.rest()),
    setSubtree(presentStackItem.subtree),
    setSelection(presentStackItem.selection),
    shouldUseReactRender,
    setActiveToolbarFromState,
    componentsChanged ? setShouldSyncToComponentMap(true) : identity,
    removedIds.length > 0 ? setRemovedComponentIds(removedIds) : identity,
    addedIds.length > 0 ? setAddedComponentIds(addedIds) : identity,
    addedIds.length > 0 ? setComponentChangeSource('undo/redo') : identity,
  ])(state);
};

export const addStackItem = (
  {
    subtree,
    selection,
    action,
    keepRedoStack = false,
  }: {
    subtree: ExpressionElement;
    selection: SelectionRecordType;
    action: unknown;
    keepRedoStack: boolean;
  },
  state: ContentEditorActive
) => {
  const redoStack = keepRedoStack
    ? state.redoStack
    : List<ContentEditorUndoStackItemRecordType>();
  return state
    .update(
      'undoStack',
      (undoStack: List<ContentEditorUndoStackItemRecordType>) =>
        undoStack.push(
          ContentEditorUndoStackItemRecord({
            subtree,
            selection,
            action,
          })
        )
    )
    .set('redoStack', redoStack);
};

const stopEmpty: Stoppable<Array<ExpressionElement>> =
  Expressions.stop(emptyArray);
const resumeEmpty: Stoppable<Array<ExpressionElement>> =
  Expressions.resume(emptyArray);
const alwaysResumeEmpty = <a>(_: a) => resumeEmpty;

export const handleNumberFieldIncrement =
  (shortcut: string) =>
  (stateAndId: ContentEditorActiveAndNextId): ContentEditorActiveAndNextId => {
    const state = getFirst(stateAndId);
    const {subtree, field, componentProp} = state;

    // Get current text value
    const currentText = getAllTextInElement(subtree);
    const currentValue = parseNumber(currentText) || 0;

    // Calculate new value based on shortcut
    let newValue: number;
    switch (shortcut) {
      case SHORTCUTS.NUMBER_INCREMENT:
        // Round up to next integer (e.g., 12.6 → 13)
        newValue = Math.ceil(currentValue);
        // If current value is already an integer, go to next one
        if (currentValue % 1 === 0 && currentValue === newValue) {
          newValue += 1;
        }
        break;
      case SHORTCUTS.NUMBER_DECREMENT:
        // Round down to previous integer (e.g., 12.6 → 12)
        newValue = Math.floor(currentValue);
        // If current value is already an integer, go to previous one
        if (currentValue % 1 === 0 && currentValue === newValue) {
          newValue -= 1;
        }
        break;
      case SHORTCUTS.NUMBER_INCREMENT_BY_TEN:
        // Round up to next integer divisible by 10
        newValue = Math.ceil(currentValue / 10) * 10;
        // If current value is already divisible by 10, go to next one
        if (currentValue % 10 === 0 && currentValue === newValue) {
          newValue += 10;
        }
        break;
      case SHORTCUTS.NUMBER_DECREMENT_BY_TEN:
        // Round down to previous integer divisible by 10
        newValue = Math.floor(currentValue / 10) * 10;
        // If current value is already divisible by 10, go to previous one
        if (currentValue % 10 === 0 && currentValue === newValue) {
          newValue -= 10;
        }
        break;
      default:
        return stateAndId;
    }

    // Convert negative zero to positive zero to avoid "-0" display
    if (Object.is(newValue, -0)) {
      newValue = 0;
    }

    // Apply field validations from CMS field or component prop constraints
    const validations = field?.get('validations');
    const componentConstraints = componentProp?.constraints;

    // Use component prop constraints if available, otherwise fall back to field validations
    if (componentConstraints || validations) {
      const constraints = {
        minValue:
          componentConstraints?.minValue ?? validations?.get('minValue'),
        maxValue:
          componentConstraints?.maxValue ?? validations?.get('maxValue'),
        allowNegative:
          componentConstraints?.allowNegative ??
          validations?.get('allowNegative'),
      };
      newValue = bindValue(newValue, constraints);
    }

    // Format the value according to field format or component prop format
    const format =
      componentConstraints?.format ?? validations?.get('format') ?? 'decimal';
    const precision =
      componentConstraints?.precision ?? validations?.get('precision');

    const formattedValue = formatNumberWithConstraints(newValue, {
      format,
      precision,
      minValue: componentConstraints?.minValue ?? validations?.get('minValue'),
      maxValue: componentConstraints?.maxValue ?? validations?.get('maxValue'),
      allowNegative:
        componentConstraints?.allowNegative ??
        validations?.get('allowNegative'),
    });

    // Replace the entire text content with the new value
    // For Number fields, we need to find the string element and replace its value
    const stringElement = findElementDescendant(subtree, isStringElement);
    if (!stringElement) {
      return stateAndId;
    }

    // Update the subtree with the new value and select all text
    return pipe([
      idPassthrough(
        updateSubtree(setStringValue(stringElement.id, formattedValue))
      ),
      selectAllSubtree,
      idPassthrough(shouldUseReactRender),
    ])(stateAndId);
  };

export const findRemoteImages = (
  siteOrDatabaseId: string,
  subtree: ExpressionElement
): Array<ExpressionElement> => {
  return Expressions.everything(concat)(
    matchExpression({
      Element: (el) => {
        if (!isImageElement(el)) return resumeEmpty;
        const srcExpr = Expressions.getIn(el.data, attrSrcPath);
        if (!srcExpr) return resumeEmpty;
        const src = Expressions.getText(srcExpr);
        const isRemoteImage =
          src &&
          src !== EXAMPLE_IMG_URL &&
          (!isWebflowImage(src) || !src.includes('/' + siteOrDatabaseId + '/'));

        if (isRemoteImage) {
          const imageFigure = getClosestFigureElement(EElement(subtree), el.id);
          return imageFigure ? Expressions.stop([imageFigure]) : stopEmpty;
        }

        return stopEmpty;
      },
      _: alwaysResumeEmpty,
    })
  )(EElement(subtree));
};
